import { DataLayer, WebPage, Product } from '@grandvisionhq/analytics';
import * as React from 'react';
import React__default, { useState, useEffect, useMemo } from 'react';
import { actions as actions$1, reducer as reducer$1, useFeatureToggles, ClickAndReserveFeatureConfigEnum, useIntl, useClickAndReserveFeatureConfig } from '@grandvisionhq/state';
import * as swcHelpers from '@swc/helpers';
import classnames from 'classnames';
import { Button, LoaderScreen, Modal, LockBodyScroll, List, toast, MediaQuery, Image, Loader, Alert, Price, ProductTag, Carousel, Util, IconButton, Expander, StickyCallToAction, Sticky } from '@grandvisionhq/common';
import { sanitizeStringForUrl, formatPrice, useLocalStorage, placeholderImage, createImageUrl, isString, parseHtml, isBoolean, getLocalizedString, isModifiedClickEvent, isNil, isDefined } from '@grandvisionhq/utils';
import { createSlotsContext } from '@grandvisionhq/slots';
import '@grandvisionhq/stores-v2';
import { useAccount, State } from '@grandvisionhq/auth';
import { Text, getSelectionBoxStyles, Heading, RoundedButton, Hr, Radio, Label, SkeletonLoader } from '@grandvisionhq/elements';
import { StockIndicator } from '@grandvisionhq/inventory';
import { useLink } from '@grandvisionhq/frk-routing';
import loadScript from 'load-script';
import { CheckboxWithLabel } from '@grandvisionhq/forms';
import { Icon, FeatureIcon } from '@grandvisionhq/icons';
import { AddToCart } from '@grandvisionhq/carts-v2';
import { ReviewSnippet, ReviewDisplay } from '@grandvisionhq/reviews';
import { gql, useLazyQuery } from '@grandvisionhq/graphql';
import { InView, useInView } from 'react-intersection-observer';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { PromoteInstallmentsLabel } from '@grandvisionhq/content';

const variantClickEvent = (event_type, product)=>({
        event_type,
        event: 'product_variation_click',
        event_category: product.type,
        product
    })
;
const pushEvent$1 = (event)=>DataLayer.pushEvent(event)
;
const ProductAnalyticsEvents = {
    pushPdpVariantClickEvent: (product)=>{
        const pdpVariantClickEvent = variantClickEvent('pdp', product);
        pushEvent$1(pdpVariantClickEvent);
    },
    pushPlpVariantClickEvent: (product)=>{
        const plpVariantClickEvent = variantClickEvent('plp', product);
        pushEvent$1(plpVariantClickEvent);
    }
};

const updateActiveVariant = actions$1.action('UPDATE_ACTIVE_VARIANT', actions$1.payload());

var actions = /*#__PURE__*/Object.freeze({
  __proto__: null,
  updateActiveVariant: updateActiveVariant
});

const initialState = {
    activeSku: ''
};
const productDetailContextReducer = reducer$1(initialState, actions$1.on(updateActiveVariant, (state, action)=>swcHelpers.objectSpread({}, state, {
        activeSku: action.payload.sku
    })
));

var reducer = /*#__PURE__*/Object.freeze({
  __proto__: null,
  initialState: initialState,
  productDetailContextReducer: productDetailContextReducer
});

/**
 * Product Context
 */ const ProductContext = /*#__PURE__*/ React.createContext({});
ProductContext.displayName = 'ProductContext';
const useProductData = ()=>{
    const context = React.useContext(ProductContext);
    if (!context) {
        throw new Error(`No ${ProductContext.displayName} available. You might need to add the ${ProductContext.displayName} provider component.`);
    }
    return context;
};
/**
 * Product Provider
 */ const ProductProvider = ({ children , sku , productUrl , product , specificationConfiguration , stockResolver , routes , colorFeatureIcons ,  })=>{
    const { variants  } = product;
    const [state, dispatch] = React.useReducer(productDetailContextReducer, swcHelpers.objectSpread({}, initialState, {
        activeSku: sku
    }));
    const activeVariant = React.useMemo(()=>variants.find((variant)=>variant.sku === state.activeSku
        ) || variants[0]
    , [
        state,
        variants
    ]);
    const value = {
        activeVariant,
        dispatch,
        productUrl,
        product,
        specificationConfiguration,
        stockResolver,
        routes,
        colorFeatureIcons
    };
    return /*#__PURE__*/ React.createElement(ProductContext.Provider, {
        value: value
    }, children);
};

/**
 * CMS Context
 */ const CmsContext = /*#__PURE__*/ React.createContext(null);
CmsContext.displayName = 'CmsContext';
const useCmsData = ()=>{
    const context = React.useContext(CmsContext);
    if (!context) {
        throw new Error(`No ${CmsContext.displayName} available. You might need to add the ${CmsContext.displayName} provider component.`);
    }
    return context;
};
/**
 * CMS Provider
 */ const CmsProvider = ({ brandPromotions , sizeGuideDocument , usps , rxGlassesConfiguratorDocument , vtoBanner , children ,  })=>{
    const value = {
        brandPromotions,
        sizeGuideDocument,
        usps,
        rxGlassesConfiguratorDocument,
        vtoBanner
    };
    return /*#__PURE__*/ React.createElement(CmsContext.Provider, {
        value: value
    }, children);
};

const WebPageSchema = ()=>{
    const { activeVariant: { description  } , product: { brandName  } , productUrl ,  } = useProductData();
    return /*#__PURE__*/ React.createElement(WebPage, {
        url: productUrl,
        pageType: "ProductDetail",
        name: brandName,
        description: description
    });
};

const [ProductDetailSlotsProvider, useProductDetailSlots] = createSlotsContext({
    displayName: 'ProductDetailSlotsContext'
});
const [ProductDetailLayoutSlotsProvider, useProductDetailLayoutSlots] = createSlotsContext({
    displayName: 'ProductDetailLayoutSlotsContext'
});

const getBrandLink = (brandName, path)=>{
    const sanitizedBrand = sanitizeStringForUrl(brandName);
    const parts = path.split('/').filter((v)=>v !== ''
    );
    const isMultilingual = /[[a-z]{2}_[A-Z]{2}/g.test(parts[0]);
    const category = isMultilingual ? parts[1] : parts[0];
    const baseUrl = `/${isMultilingual ? `${parts[0]}/` : ''}${category}`;
    return `${baseUrl}/${encodeURIComponent(sanitizedBrand)}`;
};

var ChannelAvailability;
(function(ChannelAvailability) {
    ChannelAvailability["ONLINE"] = "ONLINE";
    ChannelAvailability["IN_STORE_ONLY"] = "IN_STORE_ONLY";
})(ChannelAvailability || (ChannelAvailability = {}));
var DiscountType;
(function(DiscountType) {
    DiscountType["RELATIVE"] = "RELATIVE";
    DiscountType["ABSOLUTE"] = "ABSOLUTE";
})(DiscountType || (DiscountType = {}));

const useDiscountLabel = (value, type, currency, locale)=>{
    if (type === DiscountType.RELATIVE) {
        return `-${value}%`;
    }
    if (type === DiscountType.ABSOLUTE && !!locale) {
        const formattedDiscount = formatPrice({
            locale,
            value,
            currency,
            isCentAmount: false
        });
        return `-${formattedDiscount}`;
    }
};

const frameImages = {
    frameWidth: 'data:image/png;base64,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',
    bridgeWidth: 'data:image/png;base64,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',
    templeLength: 'data:image/png;base64,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',
    lensWidth: 'data:image/png;base64,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',
    lensHeight: 'data:image/png;base64,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'
};
const getFrameMeasurements = (bridgeWidth, frameWidth, lensHeight, lensWidth, templeLength)=>{
    if (!bridgeWidth && !frameWidth && !lensHeight && !lensWidth && !templeLength) {
        return undefined;
    }
    const frameMeasurements = [];
    if (!!frameWidth) {
        frameMeasurements.push({
            measurement: frameWidth,
            label: 'products.measurements.frame.width',
            key: 'measurements.frame.width',
            image: frameImages.frameWidth
        });
    }
    if (!!bridgeWidth) {
        frameMeasurements.push({
            measurement: bridgeWidth,
            label: 'products.measurements.bridge.width',
            key: 'measurements.bridge.width',
            image: frameImages.bridgeWidth
        });
    }
    if (!!lensHeight) {
        frameMeasurements.push({
            measurement: lensHeight,
            label: 'products.measurements.lens.height',
            key: 'measurements.lens.height',
            image: frameImages.lensHeight
        });
    }
    if (!!lensWidth) {
        frameMeasurements.push({
            measurement: lensWidth,
            label: 'products.measurements.lens.width',
            key: 'measurements.lens.width',
            image: frameImages.lensWidth
        });
    }
    if (!!templeLength) {
        frameMeasurements.push({
            measurement: templeLength,
            label: 'products.measurements.templeArm.length',
            key: 'measurements.templeArm.length',
            image: frameImages.templeLength
        });
    }
    return frameMeasurements;
};

const getActiveBrandPromotion = (brandName, brandPromotions)=>{
    if (!brandPromotions) {
        return undefined;
    }
    return brandPromotions.find(({ name  })=>name.toLowerCase() === brandName.toLowerCase()
    );
};

const defaultClickAndReserveCustomerFormContextValues = {
    Form: undefined
};
const clickAndReserveCustomerFormContext = /*#__PURE__*/ React.createContext(defaultClickAndReserveCustomerFormContextValues);
const useClickAndReserveCustomerForm = ()=>{
    const context = React.useContext(clickAndReserveCustomerFormContext);
    if (context === defaultClickAndReserveCustomerFormContextValues) {
        throw new Error(`No ClickAndReserveCustomerFormProvider available. You might still
      need to wrap the application with the ClickAndReserve form context Provider.`);
    }
    return context;
};
const ClickAndReserveCustomerFormProvider = ({ Form , children  })=>{
    const { Provider  } = clickAndReserveCustomerFormContext;
    const value = React.useMemo(()=>({
            Form
        })
    , [
        Form
    ]);
    return /*#__PURE__*/ React.createElement(Provider, {
        value: value
    }, children);
};
clickAndReserveCustomerFormContext.displayName = 'ClickAndReserveCustomerFormContext';
// @todo do I need a consumer?
clickAndReserveCustomerFormContext.Consumer;

const ClickAndReserveContextDefaultValues = {
    activeStep: 0,
    setActiveStep: ()=>null
    ,
    selectedStore: undefined,
    setSelectedStore: ()=>null
    ,
    searchLocation: undefined,
    setSearchLocation: ()=>null
    ,
    user: undefined,
    setUser: ()=>null
    ,
    customerDetailsFormState: {
        current: undefined
    },
    onChangeCustomerDetails: ()=>null
    ,
    customerDetailsPrefilled: {
        current: false
    },
    isAuthLoading: true,
    reservationNumber: undefined,
    setReservationNumber: ()=>null
    ,
    preferredChannelOfCommunication: undefined,
    setPreferredChannelOfCommunication: ()=>null
};
const ClickAndReserveContext = /*#__PURE__*/ React.createContext(ClickAndReserveContextDefaultValues);
const useClickAndReserveContext = ()=>React.useContext(ClickAndReserveContext)
;

const flowNameStock = 'store stock availability';
const flowNameReservation = 'find and reserve in store';
const events = {
    checkStoreAvailabilityClickEvent: {
        stock_only: {
            event: 'click_and_reserve_check_store_availability_click',
            site_feature: 'click and reserve',
            flow_name: flowNameStock
        },
        reservation: {
            event: 'find_and_reserve_click',
            site_feature: 'find and reserve',
            flow_name: flowNameReservation
        }
    },
    viewFindInStoreEvent: {
        stock_only: {
            event: 'store_finder_start',
            site_feature: 'store finder',
            flow_name: flowNameStock
        },
        reservation: {
            event: 'choose_store_start',
            site_feature: 'choose store',
            flow_name: flowNameReservation
        }
    },
    storeClickEvent: {
        stock_only: {
            event: 'store_finder_store_click',
            site_feature: 'store finder',
            flow_name: flowNameStock
        },
        reservation: {
            event: 'choose_store_store_click',
            site_feature: 'choose store',
            flow_name: flowNameReservation
        }
    },
    useMyCurrentLocationEvent: {
        stock_only: {
            event: 'store_finder_use_my_location_click',
            site_feature: 'store finder',
            flow_name: flowNameStock
        },
        reservation: {
            event: 'choose_store_use_my_location_click',
            site_feature: 'choose store',
            flow_name: flowNameReservation
        }
    },
    viewCustomerDetailsEvent: {
        reservation: {
            event: 'your_details_start',
            site_feature: 'your details',
            flow_name: flowNameReservation
        }
    },
    configuratorClickCloseEvent: {
        reservation: {
            event: 'your_details_close_click',
            site_feature: 'your details',
            flow_name: flowNameReservation
        }
    },
    configuratorClickBackEvent: {
        reservation: {
            event: 'your_details_back_button_click',
            site_feature: 'your details',
            flow_name: flowNameReservation
        }
    },
    requestReservationClickEvent: {
        reservation: {
            event: 'your_details_request_reservation_click',
            site_feature: 'your details',
            flow_name: flowNameReservation
        }
    },
    changeStoreClickEvent: {
        reservation: {
            event: 'your_details_change_store_click',
            site_feature: 'your details',
            flow_name: flowNameReservation
        }
    },
    viewRequestConfirmationEvent: {
        reservation: {
            event: 'request_confirmation_start',
            site_feature: 'request confirmation',
            flow_name: flowNameReservation
        }
    }
};
const pushEvent = (event)=>DataLayer.pushEvent(event)
;
const pushCheckStoreAvailabilityClickEvent = (mode)=>pushEvent(events.checkStoreAvailabilityClickEvent[mode])
;
const pushViewFindInStoreEvent = (mode)=>pushEvent(events.viewFindInStoreEvent[mode])
;
const pushStoreClickEvent = (mode, store, search)=>pushEvent(swcHelpers.objectSpread({}, events.storeClickEvent[mode], {
        store,
        search
    }))
;
const pushUseMyCurrentLocationEvent = (mode, clickLocation)=>pushEvent(swcHelpers.objectSpread({}, events.useMyCurrentLocationEvent[mode], {
        click: {
            location: clickLocation
        }
    }))
;
const pushViewCustomerDetailsEvent = ()=>pushEvent(events.viewCustomerDetailsEvent.reservation)
;
const pushConfiguratorClickCloseEvent = (stepId)=>{
    pushEvent(swcHelpers.objectSpread({}, events.configuratorClickCloseEvent.reservation, {
        step: {
            name: stepId === 1 ? 'Your details' : 'Request confirmation',
            id: stepId
        }
    }));
};
const pushConfiguratorClickBackEvent = (stepId)=>{
    pushEvent(swcHelpers.objectSpread({}, events.configuratorClickBackEvent.reservation, {
        step: {
            name: stepId === 1 ? 'Your details' : 'Request confirmation',
            id: stepId
        }
    }));
};
const pushRequestReservationClickEvent = ()=>pushEvent(events.requestReservationClickEvent.reservation)
;
const pushChangeStoreClickEvent = ()=>pushEvent(events.changeStoreClickEvent.reservation)
;
const pushViewRequestConfirmationEvent = ()=>pushEvent(events.viewRequestConfirmationEvent.reservation)
;

const ClickAndReserveProvider = ({ children  })=>{
    const [activeStep, setActiveStep] = React.useState(0);
    const [selectedStore, setSelectedStore] = React.useState();
    const [searchLocation, setSearchLocation] = React.useState();
    const [user, setUser] = React.useState();
    const [reservationNumber, setReservationNumber] = React.useState();
    const [preferredChannelOfCommunication, setPreferredChannelOfCommunication] = React.useState();
    /* see ClickAndReserveCustomerDetails/useCommitFormState.tsx for an explanation of why we use
  a ref instead of state */ const customerDetailsFormState = React.useRef();
    const onChangeCustomerDetails = React.useCallback((state)=>{
        customerDetailsFormState.current = state;
    }, []);
    /* form is prefilled with customer data if logged in, then the customer can edit
  the form and we shouldn't prefill it again so we don't destroy his changes */ const customerDetailsPrefilled = React.useRef(false);
    const { isMyAccountEnabled  } = useFeatureToggles();
    const authState = useAccount();
    const isAuthLoading = isMyAccountEnabled ? authState === 'UNKNOWN' : false;
    const isLoggedIn = isMyAccountEnabled && isAuthLoading ? undefined : authState === State.AuthState.LoggedIn;
    return /*#__PURE__*/ React.createElement(ClickAndReserveContext.Provider, {
        value: {
            activeStep,
            setActiveStep,
            selectedStore,
            setSelectedStore,
            searchLocation,
            setSearchLocation,
            user,
            setUser,
            customerDetailsFormState,
            onChangeCustomerDetails,
            customerDetailsPrefilled,
            isAuthLoading,
            isLoggedIn,
            reservationNumber,
            setReservationNumber,
            preferredChannelOfCommunication,
            setPreferredChannelOfCommunication
        }
    }, children);
};

const getClickAndReserveCtaLabelKey = (clickAndReserveFeatureConfig)=>{
    switch(clickAndReserveFeatureConfig){
        case ClickAndReserveFeatureConfigEnum.STOCK:
            return 'products.actions.checkStoreAvailability';
        case ClickAndReserveFeatureConfigEnum.STOCK_RESERVATION:
            return 'products.actions.checkStoreAvailabilityReservation';
        case ClickAndReserveFeatureConfigEnum.STOCK_RESERVATION_APPOINTMENT:
            // TODO: Add this label once we move to increment 3.
            return 'products.actions.checkStoreAvailabilityReservationAppointments';
        default:
            return '';
    }
};

const ClickAndReserveButtonContainer = ({ onClick  })=>{
    const { featureConfigs  } = useFeatureToggles();
    const { getLabel  } = useIntl();
    const ctaLabelKey = getClickAndReserveCtaLabelKey(featureConfigs === null || featureConfigs === void 0 ? void 0 : featureConfigs.clickAndReserve);
    const clickAndReserveCtaLabel = ctaLabelKey ? getLabel(ctaLabelKey) : '';
    return /*#__PURE__*/ React.createElement(Button, {
        caption: clickAndReserveCtaLabel,
        variant: "ghost",
        icon: "store",
        color: "primary",
        onClick: onClick
    });
};

const ClickAndReserveStoreFinderModal = /*#__PURE__*/ React.lazy(()=>import('./ClickAndReserveModalContainer-dacf7ae4.js')
);
const ClickAndReserveConfigurator = /*#__PURE__*/ React.lazy(()=>import('./ClickAndReserveConfigurator-41ae5d7d.js')
);
const ClickAndReserveStoreFinderWrapper = /*#__PURE__*/ React.lazy(()=>import('./ClickAndReserveStoreFinderWrapper-43c6f157.js')
);
const Fallback = ()=>/*#__PURE__*/ React.createElement(LoaderScreen, {
        height: 100
    })
;
const ClickAndReserveContainer = ({ sku , onInvalidClick  })=>{
    const { featureConfigs , isClickAndReserveEnabled: isFeatureToggleEnabled  } = useFeatureToggles();
    const [isModalOpen, setModalOpen] = React.useState(false);
    const [hasInteracted, setHasInteracted] = React.useState(false);
    const clickAndReserveMode = useClickAndReserveFeatureConfig();
    const { activeVariant: { isReservable  } ,  } = useProductData();
    const handleOnButtonOnClick = React.useCallback(()=>{
        if (!!onInvalidClick) {
            onInvalidClick();
            return;
        }
        if (clickAndReserveMode) {
            pushCheckStoreAvailabilityClickEvent(clickAndReserveMode);
            setModalOpen(true);
            !hasInteracted && setHasInteracted(true);
        }
    }, [
        onInvalidClick,
        clickAndReserveMode,
        hasInteracted
    ]);
    const handleOnModalClose = React.useCallback(()=>{
        setModalOpen(false);
    }, [
        setModalOpen
    ]);
    var ref;
    const showClickAndReserveButton = isFeatureToggleEnabled && ((ref = featureConfigs === null || featureConfigs === void 0 ? void 0 : featureConfigs.clickAndReserve) !== null && ref !== void 0 ? ref : '') in ClickAndReserveFeatureConfigEnum && isReservable;
    if (!showClickAndReserveButton) {
        return null;
    }
    return /*#__PURE__*/ React.createElement("div", null, /*#__PURE__*/ React.createElement(ClickAndReserveButtonContainer, {
        onClick: handleOnButtonOnClick
    }), hasInteracted && clickAndReserveMode === 'stock_only' && /*#__PURE__*/ React.createElement(React.Suspense, {
        fallback: /*#__PURE__*/ React.createElement(Fallback, null)
    }, /*#__PURE__*/ React.createElement(ClickAndReserveStoreFinderWrapper, {
        sku: sku,
        key: sku
    }, /*#__PURE__*/ React.createElement(ClickAndReserveStoreFinderModal, {
        isOpen: isModalOpen,
        onModalClose: handleOnModalClose,
        sku: sku
    }))), hasInteracted && clickAndReserveMode === 'reservation' && /*#__PURE__*/ React.createElement(ClickAndReserveProvider, null, /*#__PURE__*/ React.createElement(React.Suspense, {
        fallback: /*#__PURE__*/ React.createElement(Fallback, null)
    }, /*#__PURE__*/ React.createElement(ClickAndReserveStoreFinderWrapper, {
        sku: sku,
        key: sku
    }, /*#__PURE__*/ React.createElement(ClickAndReserveConfigurator, {
        isOpen: isModalOpen,
        onModalClose: handleOnModalClose,
        sku: sku
    })))));
};

const ProductStockIndicator = ()=>{
    const { activeVariant: { stock , channelAvailability  } ,  } = useProductData();
    return /*#__PURE__*/ React__default.createElement(StockIndicator, {
        stockCount: stock.stockCount,
        allowBackOrder: stock.isAllowBackOrder,
        channelAvailability: channelAvailability
    });
};

const FacetPicker = ({ onPick , itemComponent , activeFacetTerm , items , title , className , enableStockIndicator , showFacetName ,  })=>{
    const ItemComponent = itemComponent;
    return /*#__PURE__*/ React__default.createElement("div", {
        className: classnames('facet-picker', className)
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "facet-picker__header"
    }, /*#__PURE__*/ React__default.createElement(Text, {
        variant: "body-head"
    }, title), enableStockIndicator && /*#__PURE__*/ React__default.createElement(ProductStockIndicator, null)), showFacetName && /*#__PURE__*/ React__default.createElement("div", {
        className: "facet-picker__info"
    }, activeFacetTerm), /*#__PURE__*/ React__default.createElement("div", {
        className: "facet-picker__facets"
    }, /*#__PURE__*/ React__default.createElement("ul", {
        className: classnames(`facet-picker__tiles`, {
            'facet-picker__tiles--single': items.length == 1,
            [`${className}__tiles`]: className
        }),
        "aria-label": title
    }, items.map((item, index)=>/*#__PURE__*/ React__default.createElement("li", {
            key: index,
            className: classnames(`facet-picker__item`, {
                [`${className}__item`]: className
            })
        }, /*#__PURE__*/ React__default.createElement(ItemComponent, {
            isActive: item.facetTerm === activeFacetTerm,
            item: item,
            onPick: onPick
        }))
    ))));
};

const useAccessibleName = (item)=>{
    const { getLabel  } = useIntl();
    return !!item.facetTerm ? `${item.facetTerm}` : `${item.sku} - ${getLabel('products.variant.description.screenreader.fallback')}`;
};

const FacetLink = ({ item , isActive , className , children , onPick  })=>{
    const accessibleName = useAccessibleName(item);
    const productLink = useLink({
        route: 'products.detail',
        params: {
            slug: item.slug,
            sku: item.sku,
            key: 'sunglasses'
        }
    });
    const queryString = typeof window !== 'undefined' ? window.location.search : '';
    const href = `${productLink === null || productLink === void 0 ? void 0 : productLink.href}${queryString}`;
    const { name , facetTerm , sku  } = item;
    const selectionBoxClassnames = getSelectionBoxStyles({
        isSelected: isActive,
        isDisabled: false,
        className
    });
    return /*#__PURE__*/ React__default.createElement("a", {
        href: href,
        "data-t": "facet-picker-link",
        key: item.sku,
        title: accessibleName,
        className: `facet-link ${classnames(selectionBoxClassnames)}`,
        onClick: (event)=>onPick({
                event,
                sku,
                href,
                name,
                facetTerm
            })
    }, children);
};

const FrameColorFacet = ({ item , isActive , onPick  })=>{
    const accessibleName = useAccessibleName(item);
    return /*#__PURE__*/ React__default.createElement(FacetLink, {
        item: item,
        isActive: isActive,
        onPick: onPick
    }, /*#__PURE__*/ React__default.createElement("img", {
        src: item.imageUrl,
        alt: accessibleName
    }));
};

const FacetPickerColor = ({ onPick , activeFacet , items , className , enableStockIndicator ,  })=>{
    const { getLabel  } = useIntl();
    if (items.length === 0) {
        return null;
    }
    return /*#__PURE__*/ React__default.createElement(FacetPicker, {
        itemComponent: FrameColorFacet,
        items: items,
        onPick: onPick,
        title: getLabel('products.facets.frameColor.title'),
        activeFacetTerm: activeFacet === null || activeFacet === void 0 ? void 0 : activeFacet.facetTerm,
        showFacetName: true,
        className: className,
        enableStockIndicator: enableStockIndicator
    });
};

const orderSizeFacets = (items)=>{
    const sortOrder = [
        'EXTRA_SMALL',
        'SMALL',
        'MEDIUM',
        'LARGE'
    ];
    var ref;
    return (ref = items.sort((a, b)=>{
        const aIndex = sortOrder.indexOf(`${a.facetTerm}`);
        const bIndex = sortOrder.indexOf(`${b.facetTerm}`);
        return aIndex - bIndex;
    })) !== null && ref !== void 0 ? ref : [];
};

const eventName = 'size guide';
const eventAction = 'click';
const pushSizeGuideLinkClickEvent = (sku, name)=>{
    DataLayer.pushEvent({
        event: eventName,
        eventAction,
        eventLabel: `open - ${sku} - ${name}`
    });
};
const pushBookAppointmentClickEvent = (sku, name)=>{
    DataLayer.pushEvent({
        event: eventName,
        eventAction,
        eventLabel: `book an appointment - ${sku} - ${name}`
    });
};
const pushVTOClickEvent = (sku, name)=>{
    DataLayer.pushEvent({
        event: eventName,
        eventAction,
        eventLabel: `try on you - ${sku} - ${name}`
    });
};

const dittoSDKVersion = '5.0.1';
function loadDitto(sdkUrl) {
    return window.Ditto || new Promise((resolve, reject)=>loadScript(sdkUrl, (err)=>err ? reject(err) : resolve(window.Ditto)
        )
    );
}
const dittoLocalesMap = {
    'nl-BE': 'nl-nl',
    'fr-BE': 'fr-fr',
    'en-IE': 'en-gb',
    'en-IO': 'pt-pt'
};
const getDittoLocale = (locale)=>{
    let dittoLocale = locale.toLowerCase();
    if (locale in dittoLocalesMap) {
        dittoLocale = dittoLocalesMap[locale];
    }
    return dittoLocale;
};
function createVtoServerConfig(publicRuntimeDittoConfig, locale) {
    const { apiUrl , partnerId , sdkUrl , accessKey , signatureRoute  } = publicRuntimeDittoConfig;
    const dittoLocale = getDittoLocale(locale);
    const composedSdkUrl = `${sdkUrl}/default/${dittoSDKVersion}/${dittoLocale}/api.js`;
    return {
        apiUrl,
        sdkUrl: composedSdkUrl,
        accessKey,
        partnerId,
        signatureRoute
    };
}
const vtoEvents = {
    'vto.view': ({ sku  })=>({
            eventAction: 'Available',
            eventLabel: `Product ${sku} - Available`,
            VTO: {
                configFlow: {
                    action: 'view step',
                    name: 'vto available',
                    id: '000'
                }
            }
        })
    ,
    'vto.creation.view.intro': ({ sku  })=>({
            eventAction: 'View',
            eventLabel: `Product ${sku} - Intro`,
            VTO: {
                configFlow: {
                    action: 'view step',
                    name: 'introduction',
                    id: '100'
                }
            }
        })
    ,
    'vto.creation.view.camera': ({ sku  })=>({
            eventAction: 'View',
            eventLabel: `Product ${sku} - Allow camera`,
            VTO: {
                configFlow: {
                    action: 'view step',
                    name: 'allow camera',
                    id: '200'
                }
            }
        })
    ,
    'vto.creation.view.creation': ({ sku  })=>({
            eventAction: 'View',
            eventLabel: `Product ${sku} - Creation`,
            VTO: {
                configFlow: {
                    action: 'view step',
                    name: 'creation',
                    id: '300'
                }
            }
        })
    ,
    'vto.creation.view.record': ({ sku  })=>({
            eventAction: 'View',
            eventLabel: `Product ${sku} - Record`,
            VTO: {
                configFlow: {
                    action: 'view step',
                    name: 'record',
                    id: '400'
                }
            }
        })
    ,
    'vto.creation.view.save': ({ sku  })=>({
            eventAction: 'View',
            eventLabel: `Product ${sku} - Save`,
            VTO: {
                configFlow: {
                    action: 'view step',
                    name: 'save',
                    id: '500'
                }
            }
        })
    ,
    'vto.creation.success': ({ sku  })=>({
            eventAction: 'View',
            eventLabel: `Product ${sku} - Complete`,
            VTO: {
                configFlow: {
                    action: 'view step',
                    name: 'complete',
                    id: '600'
                }
            }
        })
    ,
    'vto.creation.error': ({ sku , status , description  })=>({
            eventAction: 'Failure',
            eventLabel: `Product ${sku} - Creation: error ${status}, ${description || ''}`,
            VTO: {
                configFlow: {
                    action: 'error',
                    name: status == 'CAMERA_BLOCKED' ? 'camera access' : 'creation failed'
                }
            }
        })
    ,
    'vto.tryon.open': ({ sku  })=>({
            eventAction: 'Open',
            eventLabel: `Product ${sku}`,
            VTO: {
                configFlow: {
                    action: 'click',
                    name: 'open vto'
                }
            }
        })
    ,
    'vto.tryon.retake': ({ sku  })=>({
            eventAction: 'Retake',
            eventLabel: `Product ${sku}`,
            VTO: {
                configFlow: {
                    action: 'click',
                    name: 'retake vto'
                }
            }
        })
    ,
    'vto.tryon.delete': ({ sku  })=>({
            eventAction: 'Delete',
            eventLabel: `Product ${sku}`,
            VTO: {
                configFlow: {
                    action: 'click',
                    name: 'delete vto'
                }
            }
        })
    ,
    'vto.close': ({ sku  })=>({
            eventAction: 'Close',
            eventLabel: `Product ${sku}`,
            VTO: {
                configFlow: {
                    action: 'click',
                    name: 'close vto'
                }
            }
        })
};
const vtoEvent = (key, args)=>{
    if (!vtoEvents[key]) {
        console.warn(`cannot send event '${key}', key does not exist`);
        return;
    }
    const { eventAction , eventLabel , VTO  } = vtoEvents[key](args);
    if (window.dataLayer) {
        window.dataLayer.push({
            event: 'VTOevent',
            eventCategory: 'Virtual Try-On',
            eventAction,
            eventLabel,
            VTO
        });
    }
};

const VtoContext = /*#__PURE__*/ React.createContext(null);
VtoContext.displayName = 'VtoContext';
const useVto = ()=>{
    const context = React.useContext(VtoContext);
    if (!context) {
        throw new Error(`No ${VtoContext.displayName} available. You might need to add the ${VtoContext.displayName} provider component.`);
    }
    return context;
};
const VtoProvider = ({ dittoConfig , virtualMirrorConfig , children  })=>{
    const { locale  } = useIntl();
    const { featureConfigs , isVtoEnabled  } = useFeatureToggles();
    const isVirtualMirrorEnabled = (featureConfigs === null || featureConfigs === void 0 ? void 0 : featureConfigs.virtualTryOn) === 'VIRTUAL_MIRROR';
    const isDittoEnabled = (featureConfigs === null || featureConfigs === void 0 ? void 0 : featureConfigs.virtualTryOn) === 'DITTO';
    const [isVtoActive, setVtoActive] = React.useState(false);
    const dittoServerConfig = React.useMemo(()=>{
        if (isVtoEnabled && dittoConfig && isDittoEnabled) {
            const serverConfig = createVtoServerConfig(dittoConfig, locale);
            const getSignature = function() {
                var _ref = swcHelpers.asyncToGenerator(function*(message) {
                    return fetch(`${serverConfig.signatureRoute}?message=${message}`).then((response)=>response.json()
                    ).then(({ signature  })=>signature
                    );
                });
                return function getSignature(message) {
                    return _ref.apply(this, arguments);
                };
            }();
            return swcHelpers.objectSpread({}, serverConfig, {
                getSignature,
                isEnabled: true
            });
        }
        return {
            isEnabled: false
        };
    }, [
        dittoConfig,
        isVtoEnabled,
        locale,
        isDittoEnabled
    ]);
    const config = isVirtualMirrorEnabled ? virtualMirrorConfig : dittoServerConfig;
    const value = {
        config,
        isVtoActive,
        setVtoActive,
        isVtoFeatureEnabled: isVtoEnabled
    };
    return /*#__PURE__*/ React.createElement(VtoContext.Provider, {
        value: value
    }, children);
};

function useDittoId(onDeleteCallback) {
    const { config  } = useVto();
    const { accessKey , apiUrl , isEnabled , getSignature  } = config;
    const [shouldSkipDeleteCallback, setSkipDeleteCallback] = useState(false);
    const [dittoId, setDittoId] = useLocalStorage('vto.dittoId', undefined, (value)=>value === undefined
    );
    const deleteDittoId = React__default.useCallback(swcHelpers.asyncToGenerator(function*(skipDeleteCallback = false) {
        setSkipDeleteCallback(skipDeleteCallback);
        if (!isEnabled) {
            return;
        }
        if (dittoId) {
            setDittoId(undefined);
            const url = apiUrl + `/api/1.3/dittos/${dittoId}`;
            const headers = {
                'X-Ditto-Access-Key-Id': accessKey,
                'X-Ditto-Signature': yield getSignature(dittoId)
            };
            yield fetch(url, {
                headers,
                method: 'delete'
            });
        }
    }), [
        dittoId
    ]);
    const idRef = React__default.useRef();
    React__default.useEffect(()=>{
        if (!shouldSkipDeleteCallback && onDeleteCallback && dittoId === undefined && (idRef === null || idRef === void 0 ? void 0 : idRef.current) !== undefined) {
            onDeleteCallback();
        }
        idRef.current = dittoId;
    }, [
        dittoId
    ]);
    return {
        dittoId,
        setDittoId,
        deleteDittoId
    };
}

var VtoCreationStep;
(function(VtoCreationStep) {
    VtoCreationStep[VtoCreationStep["INTRO"] = 0] = "INTRO";
    VtoCreationStep[VtoCreationStep["CREATION"] = 1] = "CREATION";
})(VtoCreationStep || (VtoCreationStep = {}));
const DittoModal = ({ onCreate , onCancel , onError , skipIntro , sku , vtoBanner ,  })=>{
    const { config  } = useVto();
    const intl = useIntl();
    const { accessKey , apiUrl , isEnabled , sdkUrl , getSignature , partnerId  } = config;
    const [hasAgreed, setAgreement] = React.useState(false);
    const [activeStep, setActiveStep] = React.useState(skipIntro ? VtoCreationStep.CREATION : VtoCreationStep.INTRO);
    const [showErrorMessage, setShowErrorMessage] = React.useState(false);
    const [errorCode, setErrorCode] = React.useState(null);
    const imgUrl = vtoBanner === null || vtoBanner === void 0 ? void 0 : vtoBanner.image.url;
    React.useEffect(()=>{
        switch(activeStep){
            case VtoCreationStep.INTRO:
                return vtoEvent('vto.creation.view.intro', {
                    sku
                });
            case VtoCreationStep.CREATION:
                return vtoEvent('vto.creation.view.creation', {
                    sku
                });
        }
    }, [
        activeStep
    ]);
    React.useEffect(()=>{
        let isCanceled = false;
        if (!isEnabled || activeStep !== VtoCreationStep.CREATION) {
            return;
        }
        let instance;
        Promise.all([
            loadDitto(sdkUrl),
            getSignature(partnerId)
        ]).then(([ditto, clientSignature])=>{
            if (!isCanceled) {
                instance = new ditto.Scan({
                    tryOnServer: apiUrl,
                    domSelector: '.ditto-modal__target',
                    accessKey,
                    partnerSignature: clientSignature,
                    partnerId,
                    font: 'nunitosans'
                }, {
                    success: (response)=>{
                        vtoEvent('vto.creation.success', {
                            sku
                        });
                        onCreate(response.scanId);
                    },
                    close: onCancel,
                    progress: ({ status  })=>{
                        switch(status){
                            case 'INIT_CAMERA':
                                vtoEvent('vto.creation.view.camera', {
                                    sku
                                });
                                break;
                            case 'READY':
                                vtoEvent('vto.creation.view.creation', {
                                    sku
                                });
                                break;
                            case 'RECORD':
                                vtoEvent('vto.creation.view.record', {
                                    sku
                                });
                                break;
                            case 'UPLOAD_STARTED':
                                vtoEvent('vto.creation.view.save', {
                                    sku
                                });
                                break;
                            default:
                                return;
                        }
                    },
                    failure: ({ status , description  })=>{
                        setErrorCode(status);
                        vtoEvent('vto.creation.error', {
                            sku,
                            status,
                            description
                        });
                    }
                });
            }
        }).catch(onError);
        return ()=>{
            isCanceled = true;
            instance && instance.destroy();
        };
    }, [
        activeStep
    ]);
    const onButtonClick = ()=>{
        if (hasAgreed) {
            return setActiveStep(VtoCreationStep.CREATION);
        }
        setShowErrorMessage(true);
    };
    return /*#__PURE__*/ React.createElement(Modal, {
        activeTrap: !skipIntro,
        "data-t": "ditto-modal",
        isOpen: true,
        className: classnames('ditto-modal__modal', {
            'ditto-modal__modal--image': imgUrl
        }),
        showIcon: activeStep === VtoCreationStep.CREATION && errorCode !== 'CAMERA_ACCESS_DENIED' ? false : true,
        onModalClose: onCancel
    }, /*#__PURE__*/ React.createElement(LockBodyScroll, null), /*#__PURE__*/ React.createElement("div", {
        className: "ditto-modal"
    }, activeStep === VtoCreationStep.INTRO && renderPrivacy(), activeStep === VtoCreationStep.CREATION && errorCode !== 'CAMERA_ACCESS_DENIED' && /*#__PURE__*/ React.createElement("div", {
        className: "ditto-modal__target"
    }), errorCode && errorCode === 'CAMERA_ACCESS_DENIED' && /*#__PURE__*/ React.createElement("div", {
        className: "ditto-modal__error-message"
    }, intl.getLabel('vto.errors.cameraAccessDenied'))));
    function renderPrivacy() {
        return /*#__PURE__*/ React.createElement(React.Fragment, null, imgUrl && /*#__PURE__*/ React.createElement("div", {
            className: "ditto-modal__aside",
            style: {
                backgroundImage: `url(${imgUrl})`
            }
        }), /*#__PURE__*/ React.createElement("div", {
            className: classnames('ditto-modal__main', {
                'ditto-modal__main--with-error': showErrorMessage && !hasAgreed
            })
        }, /*#__PURE__*/ React.createElement("div", {
            className: "ditto-modal__titles"
        }, /*#__PURE__*/ React.createElement(Heading, {
            variant: "small",
            className: "ditto-modal__title"
        }, intl.getLabel('vto.title')), /*#__PURE__*/ React.createElement(Heading, {
            variant: "smallest",
            className: "ditto-modal__subtitle"
        }, intl.getLabel('vto.subtitle'))), /*#__PURE__*/ React.createElement(List, {
            className: "ditto-modal__usps",
            items: [
                intl.getLabel('vto.step.1'),
                intl.getLabel('vto.step.2'),
                intl.getLabel('vto.step.3'),
                intl.getLabel('vto.step.4'), 
            ],
            numerical: true
        }), /*#__PURE__*/ React.createElement(Text, {
            className: "ditto-modal__description",
            color: "text-secondary",
            variant: "caption"
        }, intl.getLabel('vto.description')), /*#__PURE__*/ React.createElement(CheckboxWithLabel, {
            id: "ditto-modal-opt-in",
            className: "ditto-modal__opt-in",
            onChange: ()=>setAgreement(!hasAgreed)
            ,
            checked: hasAgreed,
            value: "ditto-modal",
            "data-t": "ditto-modal-checkbox",
            label: intl.getFormattedLabel('vto.optIn', {
                parseHTML: true
            }),
            error: showErrorMessage && !hasAgreed,
            errorMessage: showErrorMessage && !hasAgreed ? intl.getLabel('vto.errors.terms') : ''
        }), /*#__PURE__*/ React.createElement(Button, {
            caption: intl.getLabel('vto.actions.start'),
            onClick: onButtonClick,
            "data-t": "ditto-modal-button"
        })));
    }
};

const DittoPreview = ({ sku , dittoId , onError , onDelete , onRetake  })=>{
    const { config  } = useVto();
    const { accessKey , apiUrl , sdkUrl , isEnabled , getSignature , partnerId  } = config;
    React.useEffect(()=>{
        if (!isEnabled || !dittoId) {
            return;
        }
        let instance;
        Promise.all([
            loadDitto(sdkUrl),
            getSignature(dittoId)
        ]).then(([ditto, signature])=>{
            let isStatusSuccess = false;
            instance = new ditto.Overlay({
                accessKey,
                domSelector: '.ditto-preview__target',
                font: 'nunitosans',
                glassesId: sku,
                overlaySignature: signature,
                partnerId,
                scanId: dittoId,
                tryOnServer: apiUrl
            }, {
                success: ()=>{
                    isStatusSuccess = true;
                },
                failure: ()=>{
                    if (!isStatusSuccess) {
                        vtoEvent('vto.creation.error', {
                            sku
                        });
                        onRetake();
                    }
                }
            });
        }).catch(onError);
        return ()=>{
            instance && instance.destroy();
        };
    }, [
        dittoId,
        sku
    ]);
    return /*#__PURE__*/ React.createElement("div", {
        className: "ditto-preview",
        "data-t": "ditto-preview"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "ditto-preview__target"
    }), /*#__PURE__*/ React.createElement("div", {
        className: "ditto-preview__actions"
    }, /*#__PURE__*/ React.createElement(RoundedButton, {
        variant: "solid",
        size: "small",
        className: "ditto-preview__action",
        onClick: ()=>{
            vtoEvent('vto.tryon.retake', {
                sku
            });
            onRetake();
        },
        "data-t": "ditto-preview-retake"
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "pencil"
    })), /*#__PURE__*/ React.createElement(RoundedButton, {
        variant: "solid",
        size: "small",
        className: "ditto-preview__action",
        onClick: ()=>{
            vtoEvent('vto.tryon.delete', {
                sku
            });
            onDelete();
        },
        "data-t": "ditto-preview-delete"
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "trash"
    }))));
};

const DittoContainer = ({ onClose , sku , vtoBanner  })=>{
    const [skipIntro, setSkipIntro] = React.useState(false);
    const { dittoId , setDittoId , deleteDittoId  } = useDittoId(onClose);
    const { featureConfigs  } = useFeatureToggles();
    const isDittoEnabled = (featureConfigs === null || featureConfigs === void 0 ? void 0 : featureConfigs.virtualTryOn) === 'DITTO';
    React.useEffect(()=>{
        vtoEvent('vto.tryon.open', {
            sku
        });
        return ()=>vtoEvent('vto.close', {
                sku
            })
        ;
    }, []);
    if (!isDittoEnabled) {
        return null;
    }
    return dittoId ? /*#__PURE__*/ React.createElement(DittoPreview, {
        dittoId: dittoId,
        onDelete: handleDelete,
        onError: handleError,
        onRetake: handleRetake,
        sku: sku
    }) : /*#__PURE__*/ React.createElement(DittoModal, {
        onCreate: setDittoId,
        onCancel: onClose,
        onError: handleError,
        skipIntro: skipIntro,
        vtoBanner: vtoBanner,
        sku: sku
    });
    function handleRetake() {
        deleteDittoId(true);
        setSkipIntro(true);
    }
    function handleDelete() {
        deleteDittoId();
    }
    function handleError() {
        onClose();
        // @TODO implement error logging
        toast.failed('vto.errors.connection');
    }
};

const initializationParams = (apiKey, environment, locale)=>{
    return {
        selector: '.virtual-mirror__target',
        locale,
        vmInit: {
            key: apiKey,
            environment,
            glassesEnv: 'PROD',
            source: 'FASA_SGHCOM',
            channel: 'smartshopper',
            brand: 'RETAIL'
        },
        isPictureModeEnabled: true,
        isTryOnEnabled: true,
        isVideoModeEnabled: true,
        isTakePictureEnabled: true,
        isTakeScreenshotEnabled: false,
        store: {
            id: '',
            storeId: '',
            type: 'rb'
        },
        style: '',
        fromStore: true
    };
};

const loadVirtualMirror = (sdkUrl)=>{
    return window.vmmv || new Promise((resolve, reject)=>loadScript(sdkUrl, (err)=>err ? reject(err) : resolve(window.vmmv)
        )
    );
};

const VirtualMirrorModal = ({ variants  })=>{
    const { isVtoActive , setVtoActive , config: config1  } = useVto();
    const { apiKey , environment , script  } = config1;
    const { locale  } = useIntl();
    const [vmapp1, setVmapp] = React__default.useState();
    const onModalClose = ()=>{
        var ref;
        if (vmapp1 && ((ref = vmapp1.config) === null || ref === void 0 ? void 0 : ref.onClose)) {
            vmapp1.config.onClose();
            setVtoActive(false);
        }
    };
    React__default.useEffect(()=>{
        Promise.all([
            loadVirtualMirror(script)
        ]).then(([vmmv])=>{
            try {
                const DEFAULT_INIT_PARAMS = initializationParams(apiKey, environment, locale);
                const config = swcHelpers.objectSpread({}, DEFAULT_INIT_PARAMS, variants, {
                    onClose: ()=>{
                        vmapp.unmount();
                        setVtoActive(false);
                    }
                });
                const vmapp = new vmmv.VMWidgetApp(config);
                vmapp.init();
                setVmapp(vmapp);
            } catch (err) {
                setVtoActive(false);
                console.error(err);
            }
        }).catch((err)=>{
            console.error(err);
        });
    }, [
        variants,
        locale,
        isVtoActive,
        setVtoActive,
        apiKey,
        environment,
        script
    ]);
    if (!variants) {
        return null;
    }
    return /*#__PURE__*/ React__default.createElement("div", {
        className: "virtual-mirror",
        "data-t": "virtual-mirror"
    }, isVtoActive && /*#__PURE__*/ React__default.createElement(Modal, {
        activeTrap: false,
        "data-t": "virtual-mirror-modal",
        isOpen: true,
        className: "virtual-mirror__modal",
        onModalClose: ()=>onModalClose()
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "virtual-mirror__target"
    })));
};

const defaultImageParams$1 = {
    fit: 'fill',
    format: 'png'
};
const mapGraphVariantsToVirtualMirror = (product, variants, activeSku, brandLogo)=>{
    const prices = variants.filter((variant)=>variant.isVtoEnabled
    ).map((variant)=>{
        const { price: { originalPrice , discountedPrice  } , sku ,  } = variant;
        return swcHelpers.objectSpread({
            upc: sku,
            current: {
                text: `${originalPrice.amount}`,
                value: originalPrice.amount
            }
        }, discountedPrice && {
            previous: {
                text: discountedPrice && `${discountedPrice.price.amount}`,
                value: discountedPrice && discountedPrice.price.amount
            }
        }, {
            currency: originalPrice.currencyCode
        });
    }).reduce((acc, curr)=>curr.upc === activeSku ? [
            curr,
            ...acc
        ] : [
            ...acc,
            curr
        ]
    , []);
    const products = variants.filter((variant)=>variant.isVtoEnabled
    ).map((variant)=>{
        var ref, ref1, ref2;
        const { brandModelCode , brandModelColorCode , sku , displayName , media , specifications  } = variant;
        const variantImages = ((ref = media.images) === null || ref === void 0 ? void 0 : ref.length) > 0 ? media.images : [
            placeholderImage
        ];
        const frameColors = (ref1 = specifications.find((spec)=>spec.specificationAttribute === 'frameColor'
        )) === null || ref1 === void 0 ? void 0 : ref1.value;
        const frameColor = Array.isArray(frameColors) ? frameColors.join(' / ') : frameColors;
        const lensColor = (ref2 = specifications.find((spec)=>spec.specificationAttribute === 'lensColor'
        )) === null || ref2 === void 0 ? void 0 : ref2.value;
        return {
            upc: sku,
            code: brandModelCode !== null && brandModelCode !== void 0 ? brandModelCode : '',
            name: displayName,
            colorCode: brandModelColorCode !== null && brandModelColorCode !== void 0 ? brandModelColorCode : '',
            thumbnailUrl: createImageUrl(variantImages[0].url, swcHelpers.objectSpread({}, defaultImageParams$1, {
                width: 224
            })),
            brand: {
                name: product.brandName,
                logoUrl: brandLogo !== null && brandLogo !== void 0 ? brandLogo : ''
            },
            lensColorLabel: isString(lensColor) ? lensColor : '',
            frameColorLabel: isString(frameColor) ? frameColor : '',
            styleName: '',
            isCustom: false,
            size: '58 14',
            isTransition: false,
            transitionLens: {
                isEvolve: false,
                defaultType: '',
                defaultAlphaLevel: 0,
                defaultColor: '',
                availableColors: []
            }
        };
    }).reduce((acc, curr)=>curr.upc === activeSku ? [
            curr,
            ...acc
        ] : [
            ...acc,
            curr
        ]
    , []);
    return {
        prices,
        products
    };
};

const TryOnButton = ({ variant ='outline' , onClick  })=>{
    const { getLabel  } = useIntl();
    const isPhone = MediaQuery.useIsPhone();
    const { isVtoActive , isVtoFeatureEnabled , setVtoActive  } = useVto();
    const buttonSize = isPhone ? 'small' : 'medium';
    const startVTO = ()=>{
        setVtoActive(true);
        onClick && onClick();
    };
    if (!isVtoFeatureEnabled || isVtoActive) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(Button, {
        caption: getLabel('vto.callToAction'),
        "data-t": "vto-try-on-button",
        variant: variant,
        icon: "try-on",
        color: "primary",
        size: buttonSize,
        onClick: startVTO
    });
};

const FindOutSize = ({ findOutSizeCTA , findOutSizeDescription , findOutSizeImage , findOutSizeSubtitle , findOutSizeTitle , showVTOButton , onVTOStart , pushBookAppointmentClickEvent , pushVTOClickEvent ,  })=>{
    if (!findOutSizeTitle || !findOutSizeCTA || !findOutSizeImage) {
        return null;
    }
    const description = findOutSizeDescription ? parseHtml(findOutSizeDescription.value) : null;
    return /*#__PURE__*/ React__default.createElement("div", {
        className: "find-out-size"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "find-out-size__container"
    }, /*#__PURE__*/ React__default.createElement("div", null, /*#__PURE__*/ React__default.createElement(Heading, {
        variant: "smallest",
        className: "find-out-size__title"
    }, findOutSizeTitle), /*#__PURE__*/ React__default.createElement("div", {
        className: "find-out-size__subtitle"
    }, findOutSizeSubtitle), /*#__PURE__*/ React__default.createElement("div", {
        className: "find-out-size__description"
    }, description), showVTOButton ? /*#__PURE__*/ React__default.createElement(TryOnButton, {
        variant: "solid",
        onClick: ()=>{
            onVTOStart();
            pushVTOClickEvent();
        }
    }) : /*#__PURE__*/ React__default.createElement(Button, {
        key: findOutSizeCTA.link.label,
        icon: findOutSizeCTA.icon,
        className: classnames('find-out-size__button', findOutSizeCTA.link.style),
        href: findOutSizeCTA.link.url,
        caption: findOutSizeCTA.link.label,
        variant: "solid",
        onClick: ()=>pushBookAppointmentClickEvent()
    })), /*#__PURE__*/ React__default.createElement(Image, {
        alt: "find out size image",
        className: "find-out-size__image",
        url: findOutSizeImage.url
    })));
};

const Measure = ({ measureDescription , measureDescriptionImage , measureImage , measureSteps , measureTitle ,  })=>{
    if (!measureTitle || !measureSteps || !measureImage || !measureDescriptionImage) {
        return null;
    }
    return /*#__PURE__*/ React__default.createElement("div", {
        className: "measure"
    }, /*#__PURE__*/ React__default.createElement(Heading, {
        variant: "smallest",
        className: "measure__title"
    }, measureTitle), /*#__PURE__*/ React__default.createElement("div", {
        className: "measure__container"
    }, /*#__PURE__*/ React__default.createElement(List, {
        items: measureSteps,
        numerical: true
    }), /*#__PURE__*/ React__default.createElement(Image, {
        alt: "frame measure",
        className: "measure__steps-image",
        url: measureImage.url
    })), /*#__PURE__*/ React__default.createElement(Hr, null), /*#__PURE__*/ React__default.createElement("div", {
        className: "measure__container"
    }, /*#__PURE__*/ React__default.createElement("div", null, measureDescription), /*#__PURE__*/ React__default.createElement(Image, {
        alt: "frame measure",
        className: "measure__description-image",
        url: measureDescriptionImage.url
    })));
};

const SizeGuide = ({ sizeGuideSizeCompound , sizeTitle  })=>{
    if (!sizeTitle || !sizeGuideSizeCompound) {
        return null;
    }
    return /*#__PURE__*/ React__default.createElement("div", {
        className: "size-guide"
    }, /*#__PURE__*/ React__default.createElement(Heading, {
        align: "center",
        variant: "smallest",
        className: "size-guide__title"
    }, sizeTitle), /*#__PURE__*/ React__default.createElement(Hr, null), /*#__PURE__*/ React__default.createElement("div", {
        className: "size-guide__container"
    }, sizeGuideSizeCompound.map((item)=>/*#__PURE__*/ React__default.createElement("div", {
            className: "size-guide__item",
            key: item.size
        }, /*#__PURE__*/ React__default.createElement(Image, {
            alt: "frame size",
            className: "size-guide__image",
            url: item.image.url
        }), /*#__PURE__*/ React__default.createElement("div", {
            className: "size-guide__size"
        }, /*#__PURE__*/ React__default.createElement("div", {
            className: "size-guide__size-titles"
        }, /*#__PURE__*/ React__default.createElement("div", {
            className: "size-guide__size-title"
        }, item.size), /*#__PURE__*/ React__default.createElement("div", {
            className: "size-guide__size-specs"
        }, item.width)), /*#__PURE__*/ React__default.createElement("div", {
            className: "size-guide__size-description"
        }, item.description)))
    )));
};

const SizeGuideModalView = ({ sizeGuideContent , productName , sku , isVtoEnabled ,  })=>{
    const { getLabel  } = useIntl();
    const [openModal, setOpenModal] = React__default.useState(false);
    if (!sizeGuideContent) {
        return null;
    }
    const { findOutSizeCTA , findOutSizeDescription , findOutSizeImage , findOutSizeSubtitle , findOutSizeTitle , measureDescription , measureDescriptionImage , measureImage , measureSteps , measureTitle , sizeGuideSizeCompound , sizeTitle ,  } = sizeGuideContent;
    const showVTOButton = !findOutSizeCTA && isVtoEnabled;
    return /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, /*#__PURE__*/ React__default.createElement("div", {
        className: "size-guide-modal"
    }, /*#__PURE__*/ React__default.createElement("span", {
        className: "size-guide-modal__title"
    }, getLabel('products.frameSize.title')), /*#__PURE__*/ React__default.createElement(Button, {
        className: "size-guide-modal__trigger",
        variant: "ghost",
        size: "custom",
        caption: getLabel('products.frameSize.link'),
        onClick: ()=>{
            setOpenModal(true);
            pushSizeGuideLinkClickEvent(sku, productName);
        }
    })), openModal && /*#__PURE__*/ React__default.createElement(Modal, {
        className: "size-guide-modal__modal",
        isOpen: openModal,
        showIcon: true,
        onModalClose: ()=>setOpenModal(false)
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "size-guide-modal__modal-container"
    }, /*#__PURE__*/ React__default.createElement(SizeGuide, {
        sizeGuideSizeCompound: sizeGuideSizeCompound,
        sizeTitle: sizeTitle
    }), /*#__PURE__*/ React__default.createElement(Measure, {
        measureDescription: measureDescription,
        measureDescriptionImage: measureDescriptionImage,
        measureImage: measureImage,
        measureSteps: measureSteps,
        measureTitle: measureTitle
    }), /*#__PURE__*/ React__default.createElement(FindOutSize, {
        findOutSizeCTA: findOutSizeCTA,
        findOutSizeDescription: findOutSizeDescription,
        findOutSizeImage: findOutSizeImage,
        findOutSizeSubtitle: findOutSizeSubtitle,
        findOutSizeTitle: findOutSizeTitle,
        showVTOButton: showVTOButton,
        onVTOStart: ()=>setOpenModal(false)
        ,
        pushBookAppointmentClickEvent: ()=>pushBookAppointmentClickEvent(sku, productName)
        ,
        pushVTOClickEvent: ()=>pushVTOClickEvent(sku, productName)
    }))));
};

const SizeGuideModalContainer = ()=>{
    const { isVtoEnabled , isFrameSizeVTOEnabled  } = useFeatureToggles();
    const { sizeGuideDocument  } = useCmsData();
    const { activeVariant: { sku , displayName  } ,  } = useProductData();
    return /*#__PURE__*/ React__default.createElement(SizeGuideModalView, {
        sku: sku,
        isVtoEnabled: isVtoEnabled && isFrameSizeVTOEnabled,
        sizeGuideContent: sizeGuideDocument,
        productName: displayName
    });
};

const TileLayout = ({ title , subtitle , aside , iconName , className , children ,  })=>{
    return /*#__PURE__*/ React__default.createElement("div", {
        className: classnames('tile-layout', className)
    }, iconName && /*#__PURE__*/ React__default.createElement("div", {
        className: "tile-layout__aside tile-layout__aside--icon"
    }, /*#__PURE__*/ React__default.createElement(Icon, {
        name: iconName,
        className: "tile-layout__icon"
    })), /*#__PURE__*/ React__default.createElement("div", {
        className: "tile-layout__main"
    }, /*#__PURE__*/ React__default.createElement(Text, {
        className: "tile-layout__title",
        variant: "caption-head"
    }, title), subtitle && /*#__PURE__*/ React__default.createElement(Text, {
        className: "tile-layout__subtitle",
        variant: "caption"
    }, subtitle), children), aside && /*#__PURE__*/ React__default.createElement("div", {
        className: "tile-layout__aside"
    }, aside));
};

const SelectionBoxRadio = (props)=>{
    const classNames = getSelectionBoxStyles({
        isSelected: props.checked,
        isDisabled: props.disabled,
        className: props.className
    });
    return /*#__PURE__*/ React__default.createElement(Radio, swcHelpers.extends({}, props, {
        className: classNames
    }));
};

const SunglassesRxableSelect = ({ className , items , selectedValue , onChange ,  })=>{
    return /*#__PURE__*/ React__default.createElement("fieldset", {
        className: classnames('sunglasses-rxable-select', className)
    }, items.map(({ value , label , iconName  })=>{
        return /*#__PURE__*/ React__default.createElement(SelectionBoxRadio, {
            key: value,
            name: "sunglasses-rxable-select",
            value: value,
            checked: selectedValue === value,
            onChange: ()=>onChange(value)
            ,
            "data-t": value
        }, /*#__PURE__*/ React__default.createElement(TileLayout, {
            title: label,
            iconName: iconName
        }));
    }));
};

const WITH_RX$1 = 'with-rx';
const WITHOUT_RX$1 = 'without-rx';

const SunglassesRxableSelectContainer = (props)=>{
    const { getLabel  } = useIntl();
    const radioButtonItems = React__default.useMemo(()=>[
            {
                value: WITHOUT_RX$1,
                label: getLabel('products.selectRxAble.withoutRx'),
                iconName: 'sunglasses'
            },
            {
                value: WITH_RX$1,
                label: getLabel('products.selectRxAble.withRx'),
                iconName: 'store-eye-test'
            }, 
        ]
    , [
        getLabel
    ]);
    return /*#__PURE__*/ React__default.createElement(SunglassesRxableSelect, swcHelpers.extends({
        items: radioButtonItems
    }, props));
};

const NonRxProductAddToCartButton = ({ activeSku , offeringSku , bundleLineItemSkus , onSuccess ,  })=>{
    const { getLabel  } = useIntl();
    let lineItemDraft;
    if (!bundleLineItemSkus || bundleLineItemSkus.length === 0) {
        // Add a Regular product to cart
        const draft = {
            sku: activeSku,
            quantity: 1,
            type: 'regular'
        };
        lineItemDraft = draft;
    } else {
        // Add a Non-Rx Bundle product to cart
        const skus = offeringSku ? [
            offeringSku,
            ...bundleLineItemSkus
        ] : bundleLineItemSkus;
        const draft = {
            sku: activeSku,
            quantity: 1,
            type: 'non-prescription-bundle',
            bundleLineItems: skus.map((sku)=>({
                    sku,
                    quantity: 1
                })
            )
        };
        lineItemDraft = draft;
    }
    return /*#__PURE__*/ React.createElement(AddToCart, {
        caption: getLabel('products.actions.addToCart'),
        onSuccess: onSuccess,
        lineItemDrafts: [
            lineItemDraft
        ]
    });
};

const NonRxProductCTA = ({ action , addToCartConfig , storesRoute , handleConversionEvent ,  })=>{
    const { getLabel  } = useIntl();
    if (action === 'ADD_TO_CART') {
        // Add to Cart button
        return /*#__PURE__*/ React.createElement(NonRxProductAddToCartButton, swcHelpers.extends({}, addToCartConfig));
    }
    // Find a Store button
    return /*#__PURE__*/ React.createElement(Button, {
        onClick: handleConversionEvent,
        caption: getLabel('products.actions.excludedFromCheckout'),
        "data-t": "btn-excluded-from-checkout",
        href: storesRoute
    });
};

const RxGlassesCTA = ({ action , bookAppointmentRoute , onClickWhenOrderable , buttonRef , className , handleConversionEvent ,  })=>{
    const { getLabel  } = useIntl();
    return action === 'OPEN_CONFIGURATOR' ? // Open Configurator button
    /*#__PURE__*/ React.createElement(Button, {
        caption: getLabel('products.actions.openConfigurator'),
        "data-t": "btn-open-configurator",
        onClick: onClickWhenOrderable,
        btnRef: buttonRef,
        className: className
    }) : // Book Appointment button
    /*#__PURE__*/ React.createElement(Button, {
        caption: getLabel('products.actions.bookAppointment'),
        "data-t": "btn-book-examination",
        href: bookAppointmentRoute,
        className: className,
        onClick: handleConversionEvent
    });
};

const CTA = ({ isLoading , shouldOpenConfigurator , buttonRef , handleConversionEvent , handleConfiguratorOpen , onAddToCartSuccess  })=>{
    const { activeVariant: { channelAvailability , sku  } , routes , productUrl ,  } = useProductData();
    return /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, isLoading ? /*#__PURE__*/ React__default.createElement(Loader, null) : shouldOpenConfigurator ? /*#__PURE__*/ React__default.createElement(RxGlassesCTA, {
        bookAppointmentRoute: routes['book-appointment'],
        action: channelAvailability === ChannelAvailability.ONLINE ? 'OPEN_CONFIGURATOR' : 'OPEN_CONFIGURATOR_UNAVAILABLE',
        buttonRef: buttonRef,
        handleConversionEvent: handleConversionEvent,
        onClickWhenOrderable: handleConfiguratorOpen
    }) : /*#__PURE__*/ React__default.createElement(NonRxProductCTA, {
        storesRoute: routes.stores,
        action: channelAvailability === ChannelAvailability.ONLINE ? 'ADD_TO_CART' : 'ADD_TO_CART_UNAVAILABLE',
        handleConversionEvent: handleConversionEvent,
        addToCartConfig: {
            activeSku: sku,
            variantUrl: productUrl,
            onSuccess: onAddToCartSuccess
        }
    }));
};

const Discount = ({ className , discount , isWide =false , label  })=>/*#__PURE__*/ React__default.createElement("div", {
        className: classnames('product-block-discount', className)
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "product-block-discount__value"
    }, discount), label && /*#__PURE__*/ React__default.createElement("div", {
        className: classnames('product-block-discount__label', {
            'product-block-discount__label--wide': isWide
        })
    }, label))
;

const BrandPromotion = ({ brandPromotions =[] , brandName , isClickable , className ,  })=>{
    const { getFormattedLabel  } = useIntl();
    const brand = getActiveBrandPromotion(brandName, brandPromotions);
    // when no brand data render nothing
    if (!brand) {
        return null;
    }
    // get the correct imageUrl (clickable vs default)
    const imageUrl = isClickable ? brand.clickableLogo : brand.logo;
    if (!imageUrl) {
        return null;
    }
    const labelOptions = {
        hideLabelKey: true,
        defaultValue: `${brandName} reseller`,
        labelData: {
            brand: brandName
        }
    };
    const imageAlt = isClickable ? getFormattedLabel('products.brand.promotionLogo.verify', labelOptions) : getFormattedLabel('products.brand.promotionLogo', labelOptions);
    const image = /*#__PURE__*/ React__default.createElement("img", {
        src: imageUrl,
        alt: imageAlt,
        loading: "lazy"
    });
    return /*#__PURE__*/ React__default.createElement("div", {
        className: `brand-promotion ${className ? className : ''}`
    }, (brand === null || brand === void 0 ? void 0 : brand.url) && isClickable ? /*#__PURE__*/ React__default.createElement("a", {
        href: brand.url,
        target: "_blank",
        rel: "noreferrer"
    }, image) : /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, image));
};

const BrandPromotionContainer = ({ className , isClickable  })=>{
    const { brandPromotions  } = useCmsData();
    const { product: { brandName  } ,  } = useProductData();
    return /*#__PURE__*/ React__default.createElement(BrandPromotion, {
        brandPromotions: brandPromotions,
        brandName: brandName,
        className: className,
        isClickable: isClickable
    });
};

const ProductDetailTitle = ({ title , subtitle , href , brandName , brandPromotions ,  })=>/*#__PURE__*/ React.createElement("div", {
        className: "product-detail-title"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "product-detail-title__subtitle-row"
    }, !!href ? /*#__PURE__*/ React.createElement("a", {
        href: href,
        className: "product-detail-title__link"
    }, subtitle) : /*#__PURE__*/ React.createElement("span", {
        className: "product-detail-title__subtitle"
    }, subtitle), brandPromotions && brandName && /*#__PURE__*/ React.createElement(BrandPromotion, {
        className: "product-detail-title__brand-promotion",
        brandName: brandName,
        brandPromotions: brandPromotions
    })), /*#__PURE__*/ React.createElement(Heading, {
        variant: "small",
        component: "h1",
        className: "product-detail-title__title"
    }, title))
;

const ProductDetailTitleContainer = ()=>{
    const { activeVariant: { displayName  } , product: { brandName  } ,  } = useProductData();
    const { brandPromotions  } = useCmsData();
    const brandUrl = React.useMemo(()=>typeof window === 'undefined' ? undefined : getBrandLink(brandName, window.location.pathname)
    , [
        brandName
    ]);
    return /*#__PURE__*/ React.createElement(ProductDetailTitle, {
        title: displayName,
        subtitle: brandName,
        href: brandUrl,
        brandName: brandName,
        brandPromotions: brandPromotions
    });
};

const WITH_RX = 'with-rx';
const WITHOUT_RX = 'without-rx';

const isRxRadioSelection = (value)=>value === WITH_RX || value === WITHOUT_RX
;

const FrameSizeFacet = ({ item , isActive , className , onPick  })=>{
    const { getLabel  } = useIntl();
    const tileSubtitle = React__default.useMemo(()=>{
        switch(item.facetTerm){
            case 'LARGE':
                return getLabel('products.facets.frameSize.wide.subTitle');
            case 'MEDIUM':
                return getLabel('products.facets.frameSize.average.subTitle');
            case 'SMALL':
                return getLabel('products.facets.frameSize.narrow.subTitle');
            default:
                return undefined;
        }
    }, [
        getLabel,
        item.facetTerm
    ]);
    const tileTitle = React__default.useMemo(()=>{
        switch(item.facetTerm){
            case 'LARGE':
                return getLabel('products.facets.frameSize.wide.title');
            case 'MEDIUM':
                return getLabel('products.facets.frameSize.average.title');
            case 'SMALL':
                return getLabel('products.facets.frameSize.narrow.title');
            default:
                return `${item.facetTerm}`;
        }
    }, [
        getLabel,
        item.facetTerm
    ]);
    return /*#__PURE__*/ React__default.createElement(FacetLink, {
        item: item,
        isActive: isActive,
        onPick: onPick,
        className: className
    }, /*#__PURE__*/ React__default.createElement(TileLayout, {
        title: tileTitle,
        subtitle: tileSubtitle
    }));
};

const FacetPickerSize = ({ onPick , activeFacet , items: facetList , enableStockIndicator , notificationType , className ,  })=>{
    const { getLabel  } = useIntl();
    const activeFacetTerm = activeFacet === null || activeFacet === void 0 ? void 0 : activeFacet.facetTerm;
    const iconName = notificationType === 'error' ? 'alert-circle' : 'info';
    const items = React__default.useMemo(()=>{
        return orderSizeFacets(facetList !== null && facetList !== void 0 ? facetList : []);
    }, [
        facetList
    ]);
    if (facetList.length === 0) {
        return null;
    }
    return /*#__PURE__*/ React__default.createElement("div", {
        className: className
    }, /*#__PURE__*/ React__default.createElement(FacetPicker, {
        className: "facet-picker-size",
        itemComponent: FrameSizeFacet,
        items: items,
        onPick: onPick,
        title: getLabel('products.facets.frameSize.title'),
        activeFacetTerm: activeFacetTerm,
        showFacetName: false,
        enableStockIndicator: enableStockIndicator
    }), notificationType && !activeFacetTerm && /*#__PURE__*/ React__default.createElement(Alert, {
        type: notificationType,
        icon: iconName,
        message: getLabel('products.facets.frameSize.notification')
    }), /*#__PURE__*/ React__default.createElement(SizeGuideModalContainer, null));
};

const ProductDetailPrice = ({ originalPrice , discountedPrice , currency , customSize , centAmount =true , className ,  })=>{
    if (originalPrice && discountedPrice && originalPrice !== discountedPrice) {
        return /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, /*#__PURE__*/ React__default.createElement(Price, {
            value: discountedPrice,
            currency: currency,
            centAmount: centAmount,
            className: classnames('product-detail-price', className),
            size: customSize !== null && customSize !== void 0 ? customSize : 'large',
            type: "discount"
        }), /*#__PURE__*/ React__default.createElement(Price, {
            value: originalPrice,
            currency: currency,
            centAmount: centAmount,
            className: classnames('product-detail-price', className),
            size: customSize !== null && customSize !== void 0 ? customSize : 'large',
            type: "strikethrough"
        }));
    }
    return /*#__PURE__*/ React__default.createElement(Price, {
        value: originalPrice,
        currency: currency,
        centAmount: centAmount,
        size: customSize !== null && customSize !== void 0 ? customSize : 'large',
        className: "product-detail-price"
    });
};

const ProductDiscountLabel = ({ title , modifier  })=>title ? /*#__PURE__*/ React.createElement("div", {
        className: classnames('product-discount-label', {
            [`product-discount-label--${modifier}`]: !!modifier
        })
    }, /*#__PURE__*/ React.createElement("span", {
        className: "product-discount-label__title"
    }, title)) : null
;

const SpecificationItem = ({ label , value  })=>/*#__PURE__*/ React.createElement("div", {
        className: "product-detail-specification__item",
        "data-t": "specification-item",
        role: "presentation"
    }, /*#__PURE__*/ React.createElement("dt", {
        className: "product-detail-specification__item--label"
    }, label), /*#__PURE__*/ React.createElement("dd", {
        className: "product-detail-specification__item--value"
    }, Array.isArray(value) ? value.join(', ') : value))
;

const ProductDetailSpecification = ({ className , items  })=>{
    const { getLabel  } = useIntl();
    const title = getLabel('products.detail.specifications.title');
    return /*#__PURE__*/ React.createElement("section", {
        className: classnames('product-detail-specification', className),
        "aria-label": title,
        "data-t": "product-detail-specification"
    }, /*#__PURE__*/ React.createElement(Heading, {
        variant: "smallest",
        className: "product-detail-specification__title"
    }, title), /*#__PURE__*/ React.createElement("dl", {
        className: "product-detail-specification__list"
    }, items));
};

const ProductDetailSpecificationContainer = ({ className  })=>{
    const { getLabel  } = useIntl();
    const { activeVariant: { specifications  } , specificationConfiguration ,  } = useProductData();
    if (!specificationConfiguration.length || !specifications.length) {
        return null;
    }
    const items = specificationConfiguration.map((configItem)=>{
        const specItem = specifications.find((spec)=>spec.specificationAttribute === configItem.attribute
        );
        if (!specItem) {
            return;
        }
        let value = specItem.value;
        if (isBoolean(value)) {
            value = specItem.value ? getLabel('common.yes') : getLabel('common.no');
        }
        return /*#__PURE__*/ React.createElement(SpecificationItem, {
            label: getLabel(configItem.labelKey),
            value: value,
            key: configItem.attribute
        });
    }).filter(Boolean);
    if (!items.length) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(ProductDetailSpecification, {
        className: className,
        items: items
    });
};

const ProductDetailDescription = ({ className , children , description , header , title: propTitle , usps ,  })=>{
    const { getLabel  } = useIntl();
    // TODO: replace this label with something more generic
    const title = propTitle || getLabel('products.contactLenses.header.details');
    return /*#__PURE__*/ React.createElement("section", {
        className: classnames('product-detail-description', className),
        "aria-label": title,
        "data-t": "product-detail-description"
    }, title && /*#__PURE__*/ React.createElement(Heading, {
        variant: "smallest",
        component: "h2"
    }, title), usps && /*#__PURE__*/ React.createElement(List, {
        className: "product-detail-description__list",
        icon: "checkmark",
        items: usps
    }), header && /*#__PURE__*/ React.createElement(Heading, {
        variant: "subheading1",
        className: "product-detail-description__subheading",
        component: "h3"
    }, header), description && /*#__PURE__*/ React.createElement(Text, {
        variant: "body"
    }, description), children);
};

const ProductDetailDescriptionContainer = ({ className , children  })=>{
    const { activeVariant  } = useProductData();
    const { description , shortDescription , variantUsps  } = activeVariant;
    return /*#__PURE__*/ React__default.createElement(ProductDetailDescription, {
        className: className,
        header: shortDescription,
        description: description,
        usps: variantUsps,
        children: children
    });
};

/**
 *
 * These labels are dynamically generated:
 *   products.sunglasses.features.uvCategory
 *   products.sunglasses.features.polarized
 *   products.sunglasses.features.glazeable
 *
 */ const ProductDetailFeatures = ({ productFeatures , colorFeatureIcons ='orange' ,  })=>{
    const { getLabel  } = useIntl();
    return /*#__PURE__*/ React.createElement("ul", {
        className: "product-detail-features"
    }, productFeatures.map(({ type , iconName  })=>/*#__PURE__*/ React.createElement("li", {
            className: "product-detail-features__feature",
            key: type
        }, /*#__PURE__*/ React.createElement(FeatureIcon, {
            name: iconName,
            color: colorFeatureIcons,
            size: 38
        }), getLabel(`products.sunglasses.features.${type}`))
    ));
};

const ProductDetailFeaturesContainer = ()=>{
    const { activeVariant: { productFeatures  } , colorFeatureIcons ,  } = useProductData();
    if (!productFeatures.length) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(ProductDetailFeatures, {
        productFeatures: productFeatures,
        colorFeatureIcons: colorFeatureIcons
    });
};

const ProductDetailUspItem = ({ item  })=>{
    const { icon , text  } = item;
    return /*#__PURE__*/ React.createElement("div", {
        className: "product-detail-usps__item"
    }, icon && /*#__PURE__*/ React.createElement("span", {
        className: "product-detail-usps__icon"
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: icon,
        width: 24,
        height: 24
    })), /*#__PURE__*/ React.createElement("span", {
        className: "product-detail-usps__message"
    }, text));
};

const ProductDetailUsps = ({ items , layout ='list'  })=>{
    return /*#__PURE__*/ React.createElement("div", {
        className: classnames('product-detail-usps', `product-detail-usps--layout-${layout}`, {
            [`product-detail-usps--columns-3`]: layout === 'grid'
        })
    }, items.map((usp)=>/*#__PURE__*/ React.createElement(ProductDetailUspItem, {
            item: usp,
            key: usp.text
        })
    ));
};

const ProductDetailUspsContainer = ({ layout  })=>{
    const { usps  } = useCmsData();
    if (!usps || !(usps === null || usps === void 0 ? void 0 : usps.length)) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(ProductDetailUsps, {
        items: usps,
        layout: layout
    });
};

const PriceSection = ()=>{
    var ref, ref1, ref2, ref3, ref4, ref5, ref6, ref7, ref8, ref9, ref10, ref11, ref12, ref13;
    const { getLabel , locale  } = useIntl();
    const { activeVariant: { price  } ,  } = useProductData();
    const { isProductLabelEnabled  } = useFeatureToggles();
    const { Slots  } = useProductDetailSlots();
    const discount = useDiscountLabel((ref = price.discountedPrice) === null || ref === void 0 ? void 0 : (ref1 = ref.discountInformation) === null || ref1 === void 0 ? void 0 : ref1.value, (ref2 = price.discountedPrice) === null || ref2 === void 0 ? void 0 : (ref3 = ref2.discountInformation) === null || ref3 === void 0 ? void 0 : ref3.type, (ref4 = price.discountedPrice) === null || ref4 === void 0 ? void 0 : (ref5 = ref4.price) === null || ref5 === void 0 ? void 0 : ref5.currencyCode, locale);
    const discountIsZero = ((ref6 = price.discountedPrice) === null || ref6 === void 0 ? void 0 : (ref7 = ref6.price) === null || ref7 === void 0 ? void 0 : ref7.amount) === ((ref8 = price.originalPrice) === null || ref8 === void 0 ? void 0 : ref8.amount);
    return /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, /*#__PURE__*/ React__default.createElement("div", {
        className: "price-section",
        role: "group",
        "aria-label": getLabel('products.screenreader.priceInformation')
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "price-section__wrapper"
    }, /*#__PURE__*/ React__default.createElement(ProductDetailPrice, {
        centAmount: false,
        originalPrice: price.originalPrice.amount,
        discountedPrice: (ref9 = price.discountedPrice) === null || ref9 === void 0 ? void 0 : ref9.price.amount,
        currency: price.originalPrice.currencyCode
    })), !!isProductLabelEnabled && /*#__PURE__*/ React__default.createElement(ProductDiscountLabel, {
        title: getLocalizedString((ref10 = price.discountedPrice) === null || ref10 === void 0 ? void 0 : (ref11 = ref10.discountInformation) === null || ref11 === void 0 ? void 0 : ref11.name, locale)
    }), !isProductLabelEnabled && !!discount && !discountIsZero && /*#__PURE__*/ React__default.createElement(Discount, {
        discount: discount,
        label: getLocalizedString((ref12 = price.discountedPrice) === null || ref12 === void 0 ? void 0 : (ref13 = ref12.discountInformation) === null || ref13 === void 0 ? void 0 : ref13.description, locale),
        isWide: true
    })), Slots.AfterPrice && /*#__PURE__*/ React__default.createElement(Slots.AfterPrice, null));
};

const StarRatingReview = ({ className  })=>{
    const { activeVariant: { sku  } ,  } = useProductData();
    return /*#__PURE__*/ React__default.createElement(ReviewSnippet, {
        className: className,
        productId: sku,
        variantId: sku
    });
};

const UserReviews = ()=>{
    const { isReviewsEnabled  } = useFeatureToggles();
    const { activeVariant: { sku  } ,  } = useProductData();
    if (!isReviewsEnabled) {
        return null;
    }
    return /*#__PURE__*/ React__default.createElement(ReviewDisplay, {
        productId: sku,
        variantId: sku
    });
};

const Tags = ()=>{
    const { isProductLabelEnabled  } = useFeatureToggles();
    const { activeVariant: { tags  } ,  } = useProductData();
    if (!(tags === null || tags === void 0 ? void 0 : tags.length) || tags[0].value === '' || isProductLabelEnabled) {
        return null;
    }
    return /*#__PURE__*/ React__default.createElement(ProductTag, {
        tag: tags[0]
    });
};

const getFacetPickerConfig = (facetTree, activeSku)=>{
    const activeParentFacet = facetTree.find((parentFacet)=>parentFacet.skuCollection.includes(activeSku)
    );
    const childFacetsOfActiveParent = activeParentFacet === null || activeParentFacet === void 0 ? void 0 : activeParentFacet.childFacets;
    const activeChildFacet = childFacetsOfActiveParent === null || childFacetsOfActiveParent === void 0 ? void 0 : childFacetsOfActiveParent.find((sF)=>sF.sku === activeSku
    );
    /**
   * A user only needs to select a child facet if there is more than 1 option to choose from
   */ const childFacetRequiresUserSelection = !!((childFacetsOfActiveParent === null || childFacetsOfActiveParent === void 0 ? void 0 : childFacetsOfActiveParent.length) && childFacetsOfActiveParent.length > 1);
    /**
   * Only show the parent facet picker if there is more than 1 parent facet
   */ const showParentFacetPicker = facetTree.length > 1;
    /**
   * Currently, we only support "color" as the parent facet and "size" as the child facet
   * However, this should be made more generic in the future
   */ return {
        colorFacets: facetTree,
        sizeFacets: childFacetsOfActiveParent,
        activeColorFacet: activeParentFacet,
        activeSizeFacet: activeChildFacet,
        showColorFacets: showParentFacetPicker,
        requiresUserSizeSelection: childFacetRequiresUserSelection
    };
};

const SunglassesEcommContent = ({ className , showOnlyCTA , ctaRef  })=>{
    var ref;
    const isMobile = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    const { getLabel  } = useIntl();
    const { activeVariant: { displayName , sku: activeSku , stock , isVtoEnabled  } , product , dispatch ,  } = useProductData();
    const { brandPromotions  } = useCmsData();
    const { facetTree , isOfferedWithPrescriptions , productType , brandName , variants  } = product;
    const [selectWithRxLens, setSelectWithRxLens] = React__default.useState(WITHOUT_RX);
    var ref1;
    const shouldOpenConfigurator = (ref1 = isOfferedWithPrescriptions && selectWithRxLens === WITH_RX) !== null && ref1 !== void 0 ? ref1 : false;
    const openConfiguratorButtonRef = React__default.useRef(null);
    const [configuratorIsLoading] = React__default.useState(false);
    const [notificationType, setNotificationType] = React__default.useState(undefined);
    const [userHasSelectedASize, setUserHasSelectedASize] = React__default.useState(false);
    const { Slots  } = useProductDetailSlots();
    const { featureConfigs  } = useFeatureToggles();
    const { isVtoActive , isVtoFeatureEnabled  } = useVto();
    const isVirtualMirrorEnabled = isVtoFeatureEnabled && isVtoEnabled && (featureConfigs === null || featureConfigs === void 0 ? void 0 : featureConfigs.virtualTryOn) === 'VIRTUAL_MIRROR';
    const brandLogo = (ref = getActiveBrandPromotion(brandName, brandPromotions)) === null || ref === void 0 ? void 0 : ref.logo;
    const mappedVTOVariants = isVirtualMirrorEnabled ? mapGraphVariantsToVirtualMirror(product, variants, activeSku, brandLogo) : undefined;
    const { activeColorFacet , activeSizeFacet , colorFacets , sizeFacets , showColorFacets , requiresUserSizeSelection ,  } = getFacetPickerConfig(facetTree, activeSku);
    const hasSingleSizeFacet = React__default.useMemo(()=>{
        return sizeFacets && (sizeFacets === null || sizeFacets === void 0 ? void 0 : sizeFacets.length) === 1;
    }, [
        sizeFacets
    ]);
    const showActiveSizeFacet = React__default.useMemo(()=>hasSingleSizeFacet || requiresUserSizeSelection && userHasSelectedASize
    , [
        hasSingleSizeFacet,
        requiresUserSizeSelection,
        userHasSelectedASize
    ]);
    const showSizeFacets = sizeFacets && (sizeFacets === null || sizeFacets === void 0 ? void 0 : sizeFacets.length) > 0;
    const isValid = React__default.useMemo(()=>!requiresUserSizeSelection || requiresUserSizeSelection && userHasSelectedASize
    , [
        requiresUserSizeSelection,
        userHasSelectedASize
    ]);
    const onRxRadioChange = React__default.useCallback((newSelection)=>{
        if (isRxRadioSelection(newSelection) && selectWithRxLens !== newSelection) {
            setSelectWithRxLens(newSelection);
        // triggerDataLayerEvent('click on button', { isWithRx: newSelection === WITH_RX })
        }
    }, [
        selectWithRxLens
    ]);
    const handleConfiguratorOpen = React__default.useCallback(()=>{
    // @TODO: handle conversion event in #
    // Possibly we will need to add this to products-details-v2
    }, []);
    const handleConversionEvent = React__default.useCallback(()=>{
    // @TODO: handle conversion event in #
    // Possibly we will need to add this to products-details-v2
    }, []);
    const handleVariantSelection = React__default.useCallback(({ event , sku , href  })=>{
        // Allow customers to open another variant in a new tab or window, otherwise prevent navigation
        if (!isModifiedClickEvent(event)) {
            event.preventDefault();
            dispatch(updateActiveVariant({
                sku
            }));
            window.history.replaceState(null, '', href);
        }
    }, [
        dispatch
    ]);
    const handleColorFacetSelection = (props)=>{
        const { name , sku , facetTerm  } = props;
        ProductAnalyticsEvents.pushPdpVariantClickEvent({
            type: productType,
            name,
            id: sku,
            dimension147: facetTerm
        });
        setUserHasSelectedASize(false);
        setNotificationType('info');
        handleVariantSelection(props);
    };
    const handleSizeFacetSelection = (props)=>{
        const { name , sku , facetTerm  } = props;
        ProductAnalyticsEvents.pushPdpVariantClickEvent({
            type: productType,
            name,
            id: sku,
            frameSize: facetTerm
        });
        setUserHasSelectedASize(true);
        handleVariantSelection(props);
    };
    const onAddToCartSuccess = React__default.useCallback(()=>{
    // @TODO: in a future story, this is where the data analytics will be triggered
    }, []);
    const triggerAlertMessage = React__default.useCallback(()=>{
        setNotificationType('error');
    }, []);
    const CTAButton = !isValid ? /**
     * This is a dummy button that triggers an error message if
     * the add-to-cart flow is not yet valid (i.e. the user still needs to pick a size)
     */ /*#__PURE__*/ React__default.createElement(Button, {
        onClick: triggerAlertMessage,
        caption: getLabel('products.actions.addToCart')
    }) : /*#__PURE__*/ React__default.createElement(CTA, {
        isLoading: configuratorIsLoading,
        shouldOpenConfigurator: shouldOpenConfigurator,
        handleConfiguratorOpen: handleConfiguratorOpen,
        handleConversionEvent: handleConversionEvent,
        buttonRef: openConfiguratorButtonRef,
        onAddToCartSuccess: onAddToCartSuccess
    });
    if (showOnlyCTA) {
        return /*#__PURE__*/ React__default.createElement(React__default.Fragment, null, CTAButton);
    }
    return /*#__PURE__*/ React__default.createElement("section", {
        "aria-label": displayName,
        className: classnames('sunglasses-ecomm-content', className)
    }, /*#__PURE__*/ React__default.createElement(Tags, null), /*#__PURE__*/ React__default.createElement("div", {
        className: "sunglasses-ecomm-content__section"
    }, /*#__PURE__*/ React__default.createElement(PriceSection, null), !isMobile && /*#__PURE__*/ React__default.createElement(StarRatingReview, {
        className: "sunglasses-ecomm-content__star-review"
    })), !showColorFacets && /*#__PURE__*/ React__default.createElement("div", {
        className: "sunglasses-ecomm-content__single-facet-term sunglasses-ecomm-content__section"
    }, activeColorFacet && /*#__PURE__*/ React__default.createElement("div", null, activeColorFacet.facetTerm), !showSizeFacets && /*#__PURE__*/ React__default.createElement(ProductStockIndicator, null)), showColorFacets && /*#__PURE__*/ React__default.createElement(FacetPickerColor, {
        className: "sunglasses-ecomm-content__section",
        activeFacet: activeColorFacet,
        onPick: handleColorFacetSelection,
        items: colorFacets,
        enableStockIndicator: (sizeFacets === null || sizeFacets === void 0 ? void 0 : sizeFacets.length) === 0 ? stock.enableStockIndicator : false
    }), isOfferedWithPrescriptions && /*#__PURE__*/ React__default.createElement(SunglassesRxableSelectContainer, {
        className: "sunglasses-ecomm-content__section",
        onChange: onRxRadioChange,
        selectedValue: selectWithRxLens
    }), showSizeFacets && /*#__PURE__*/ React__default.createElement(FacetPickerSize, {
        className: "sunglasses-ecomm-content__section",
        activeFacet: showActiveSizeFacet ? activeSizeFacet : undefined,
        onPick: handleSizeFacetSelection,
        items: sizeFacets,
        notificationType: requiresUserSizeSelection ? notificationType : undefined,
        enableStockIndicator: showActiveSizeFacet ? stock.enableStockIndicator : false
    }), /*#__PURE__*/ React__default.createElement("div", {
        ref: ctaRef
    }, CTAButton, isVtoEnabled && isVtoFeatureEnabled && /*#__PURE__*/ React__default.createElement(TryOnButton, null)), /*#__PURE__*/ React__default.createElement(ClickAndReserveContainer, {
        sku: activeSku,
        onInvalidClick: !isValid ? triggerAlertMessage : undefined
    }), isVirtualMirrorEnabled && isVtoActive && /*#__PURE__*/ React__default.createElement(VirtualMirrorModal, {
        variants: mappedVTOVariants
    }), Slots.AfterCTA ? /*#__PURE__*/ React__default.createElement(Slots.AfterCTA, null) : null);
};

var CartDiscountType;
(function(CartDiscountType) {
    CartDiscountType["PERCENT"] = "PERCENT";
    CartDiscountType["AMOUNT"] = "AMOUNT";
})(CartDiscountType || (CartDiscountType = {}));
function createCartDiscountError(type, originalError) {
    return {
        type,
        originalError: originalError instanceof Error ? originalError : new Error('UNKNOWN_ERROR')
    };
}

const MAX_QUANTITY_LIMIT = 99;

const getDiscountPerPack = (packPrice, discountAmount, type, priceIsCentAmount)=>{
    if (type === CartDiscountType.PERCENT) {
        return packPrice * discountAmount / 100;
    }
    return priceIsCentAmount ? discountAmount * 100 : discountAmount;
};
const getCartDiscountAmount = (packPrice, appliedDiscounts)=>{
    let totalDiscountAmount = 0;
    if (appliedDiscounts) {
        for (const appliedDiscount of appliedDiscounts){
            if (appliedDiscount.discount.minimumQuantity > 0) {
                const cartDiscountPerPackAmount = getDiscountPerPack(packPrice, appliedDiscount.discount.amount, appliedDiscount.discount.type, true);
                const netDiscountAmount = cartDiscountPerPackAmount * appliedDiscount.timesApplied * appliedDiscount.discount.minimumQuantity;
                totalDiscountAmount += netDiscountAmount;
            }
        }
    }
    return totalDiscountAmount;
};
function getTotalAvailableQuantity(config) {
    const { leftEyeChecked , rightEyeChecked  } = config;
    let availableQuantity = 0;
    const totalAvailableQuantityLeft = getAvailableQuantity(config.maxOrderableQuantity);
    const totalAvailableQuantityRight = getAvailableQuantity(config.maxOrderableQuantity);
    if (leftEyeChecked) {
        availableQuantity += totalAvailableQuantityLeft;
    }
    if (rightEyeChecked) {
        availableQuantity += totalAvailableQuantityRight;
    }
    return availableQuantity;
}
function getFilteredQuantityDiscounts(config) {
    const { quantityDiscountItems  } = config;
    const totalAvailableQuantity = getTotalAvailableQuantity(config);
    if (!quantityDiscountItems) {
        return [];
    }
    return [
        ...quantityDiscountItems
    ].filter((item)=>item.minimumQuantity <= totalAvailableQuantity
    );
}
function getMatchedDiscounts(quantity, config) {
    const orderedQuantityDiscounts = getOrderedQuantityDiscounts(config);
    const matchedDiscounts = [];
    for (const discount of orderedQuantityDiscounts){
        if (quantity >= discount.minimumQuantity) {
            const timesApplied = Math.floor(quantity / discount.minimumQuantity);
            matchedDiscounts.push({
                discount,
                timesApplied
            });
            quantity -= timesApplied * discount.minimumQuantity;
        }
    }
    return matchedDiscounts;
}
function getHighestMatchedDiscount(quantity, config) {
    const discounts = getMatchedDiscounts(quantity, config);
    if (!discounts || discounts.length === 0) {
        return undefined;
    }
    const highestDiscount = discounts.reduce((prevDiscount, currDiscount)=>{
        return prevDiscount.discount.minimumQuantity > currDiscount.discount.minimumQuantity ? prevDiscount : currDiscount;
    }, discounts[0]);
    return highestDiscount;
}
/**
 *
 * This function aims to divide an input number as evenly as possible into two parts, with one part possibly being slightly larger than the other if the input number is odd.
 * For example, if the input number is 5, the output will be [3, 2].
 * If the input number is 6, the output will be [3, 3].
 *
 * We use this to apply  the correct number of boxes to each eye when the user selects a discount item.
 */ function divideEvenlyInTwo(num) {
    return [
        Math.ceil(num / 2),
        Math.floor(num / 2)
    ];
}
function getAvailableQuantity(maxOrderableQuantity) {
    return maxOrderableQuantity ? Math.min(maxOrderableQuantity, MAX_QUANTITY_LIMIT) : MAX_QUANTITY_LIMIT;
}
function getOrderedQuantityDiscounts(config) {
    return getFilteredQuantityDiscounts(config).sort((a, b)=>b.minimumQuantity - a.minimumQuantity
    );
}

const SelectToggleButton = ({ name , children , isSelected , onItemClick , className  })=>{
    const handleClick = ()=>{
        onItemClick();
    };
    const classNames = getSelectionBoxStyles({
        isSelected,
        isDisabled: false,
        className
    });
    return /*#__PURE__*/ React__default.createElement("button", {
        className: classnames('select-toggle-button', classNames),
        onClick: handleClick,
        "aria-pressed": isSelected,
        name: name
    }, children);
};

const DiscountLabel = ({ item , currency  })=>{
    const { locale , getFormattedLabel  } = useIntl();
    if (item.type === CartDiscountType.PERCENT) {
        return /*#__PURE__*/ React__default.createElement(Text, {
            color: "primary",
            variant: "caption-head"
        }, getFormattedLabel('products.contactLenses.quantityDiscountSelect.save.percentage', {
            labelData: {
                percentage: item.amount
            }
        }));
    }
    const formattedSaving = formatPrice({
        locale,
        value: item.amount,
        currency,
        isCentAmount: false
    });
    return /*#__PURE__*/ React__default.createElement(Text, {
        color: "primary",
        variant: "caption-head"
    }, getFormattedLabel('products.contactLenses.quantityDiscountSelect.save.amount', {
        labelData: {
            amount: formattedSaving
        }
    }));
};
const QuantityDiscountItemLayout = ({ item , price , priceIsCentAmount , currency , selectedValue , onChange ,  })=>{
    const { locale , getFormattedLabel , getLabel  } = useIntl();
    const formattedPrice = formatPrice({
        locale,
        value: price - getDiscountPerPack(price, item.amount, item.type, priceIsCentAmount),
        currency,
        isCentAmount: !!priceIsCentAmount
    });
    const aside = /*#__PURE__*/ React__default.createElement(DiscountLabel, {
        item: item,
        currency: currency
    });
    const isSelected = React__default.useMemo(()=>selectedValue === item.minimumQuantity
    , [
        selectedValue,
        item.minimumQuantity
    ]);
    const title = getFormattedLabel('products.contactLenses.quantityDiscountSelect.title', {
        labelData: {
            minimumQuantity: item.minimumQuantity,
            price: formattedPrice
        }
    });
    return /*#__PURE__*/ React__default.createElement(SelectToggleButton, {
        key: item.minimumQuantity,
        isSelected: isSelected,
        onItemClick: ()=>onChange(!isSelected ? item.minimumQuantity : undefined)
        ,
        "data-t": `quantity-discount-select-${item.minimumQuantity}`,
        name: title
    }, /*#__PURE__*/ React__default.createElement(TileLayout, {
        title: title,
        subtitle: getLabel('products.contactLenses.quantityDiscountSelect.subTitle'),
        aside: aside,
        className: "quantity-discount-select__tile"
    }));
};
const QuantityDiscountSelect = ({ price , priceIsCentAmount =true , currency , className , items , selectedValue , onChange ,  })=>{
    return /*#__PURE__*/ React__default.createElement("ul", {
        className: classnames('quantity-discount-select', className)
    }, items.map((item)=>{
        return /*#__PURE__*/ React__default.createElement("li", {
            key: item.minimumQuantity
        }, /*#__PURE__*/ React__default.createElement(QuantityDiscountItemLayout, {
            item: item,
            price: price,
            priceIsCentAmount: priceIsCentAmount,
            currency: currency,
            selectedValue: selectedValue,
            onChange: onChange
        }));
    }));
};

const mapGraphCartDiscountToClientCartDiscount = (serverCartDiscounts)=>{
    return serverCartDiscounts.map(({ minimumQuantity , amount , type: type1  })=>{
        const mappedType = (type)=>{
            switch(type){
                case 'PERCENT':
                    return CartDiscountType.PERCENT;
                case 'AMOUNT':
                    return CartDiscountType.AMOUNT;
                default:
                    throw new Error(`Unknown cart discount type: ${type}`);
            }
        };
        return {
            minimumQuantity: minimumQuantity !== null && minimumQuantity !== void 0 ? minimumQuantity : 0,
            amount: amount !== null && amount !== void 0 ? amount : 0,
            type: mappedType(type1)
        };
    });
};

const GET_CART_DISCOUNTS_BY_PROMOTION_IDENTIFIER = gql`
  query GetCartDiscountsByPromotionIdentifier($promotionIdentifier: String!) {
    getCartDiscountsByPromotionIdentifier(promotionIdentifier: $promotionIdentifier) {
      amount
      minimumQuantity
      type
    }
  }
`;

/*
 * Query the cart discounts by promotion identifier
 */ const useCartDiscounts = (promotionIdentifier, enabled = false)=>{
    const [discountError, setDiscountError] = React__default.useState(null);
    const [getCartDiscounts, { data , loading , error  }] = useLazyQuery(GET_CART_DISCOUNTS_BY_PROMOTION_IDENTIFIER);
    const mappedCartDiscounts = React__default.useMemo(()=>{
        if (!(data === null || data === void 0 ? void 0 : data.getCartDiscountsByPromotionIdentifier)) {
            return [];
        }
        return mapGraphCartDiscountToClientCartDiscount(data.getCartDiscountsByPromotionIdentifier);
    }, [
        data === null || data === void 0 ? void 0 : data.getCartDiscountsByPromotionIdentifier
    ]);
    React__default.useEffect(()=>{
        if (enabled && promotionIdentifier) {
            getCartDiscounts({
                variables: {
                    promotionIdentifier
                }
            });
        }
    }, [
        promotionIdentifier,
        enabled
    ]);
    React__default.useEffect(()=>{
        if (error) {
            setDiscountError(createCartDiscountError('CART_DISCOUNT_ERROR', error));
        }
    }, [
        error
    ]);
    return React__default.useMemo(()=>{
        return {
            loading,
            cartDiscounts: mappedCartDiscounts,
            error: discountError
        };
    }, [
        loading,
        mappedCartDiscounts,
        discountError
    ]);
};

const Measurement = ({ image , label , measurement  })=>{
    const { getLabel  } = useIntl();
    const metric = getLabel('products.measurements.metric');
    return /*#__PURE__*/ React__default.createElement("section", {
        className: "product-detail-frame-measurements__item"
    }, /*#__PURE__*/ React__default.createElement("span", {
        className: "product-detail-frame-measurements__label"
    }, getLabel(label)), /*#__PURE__*/ React__default.createElement("img", {
        "aria-hidden": "true",
        src: image,
        alt: ""
    }), /*#__PURE__*/ React__default.createElement("span", {
        className: "product-detail-frame-measurements__details"
    }, measurement, " ", metric));
};

const FrameMeasurements = ({ measurements  })=>{
    const isMobile = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    const { getLabel  } = useIntl();
    const title = getLabel('products.measurements.frame.title');
    return /*#__PURE__*/ React__default.createElement("section", {
        className: "product-detail-frame-measurements",
        "aria-label": title
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "product-detail-frame-measurements__container"
    }, /*#__PURE__*/ React__default.createElement(Heading, {
        variant: "medium",
        className: "product-detail-frame-measurements__title"
    }, title), /*#__PURE__*/ React__default.createElement("div", {
        className: "product-detail-frame-measurements__items",
        role: "group"
    }, isMobile && /*#__PURE__*/ React__default.createElement(Carousel, {
        pagination: true
    }, measurements.map((item)=>/*#__PURE__*/ React__default.createElement(Measurement, swcHelpers.extends({}, item, {
            key: item.key
        }))
    )), !isMobile && measurements.map((item)=>/*#__PURE__*/ React__default.createElement(Measurement, swcHelpers.extends({}, item, {
            key: item.key
        }))
    ))));
};

const FrameMeasurementsContainer = ()=>{
    const { activeVariant: { frameMeasurements  } ,  } = useProductData();
    if (!frameMeasurements) {
        return null;
    }
    return /*#__PURE__*/ React.createElement(FrameMeasurements, {
        measurements: frameMeasurements
    });
};

const PrescriptionAvailabilityIndicator = ({ className  })=>{
    const { getLabel  } = useIntl();
    return /*#__PURE__*/ React.createElement(Label, {
        type: "neutral",
        className: classnames('prescription-availability-indicator', className)
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "store-eye-test",
        className: "prescription-availability-indicator__icon"
    }), /*#__PURE__*/ React.createElement(Text, {
        variant: "body-head"
    }, getLabel('products.prescriptionAvailable')));
};

const pushImageGalleryItemExpandedEvent = (isExpanded)=>{
    DataLayer.pushEvent({
        event: 'image gallery',
        eventAction: 'click',
        eventLabel: isExpanded ? 'show more' : 'show less'
    });
};
const pushImageGalleryOpenEvent = (altText, index, sku)=>{
    DataLayer.pushEvent({
        event: 'image gallery',
        eventAction: 'open',
        eventLabel: `${sku} - ${altText} - ${index}`
    });
};
const pushImageGalleryZoomEvent = (altText, sku)=>{
    DataLayer.pushEvent({
        event: 'image gallery',
        eventAction: 'zoom',
        eventLabel: `${sku} - ${altText}`
    });
};
const pushImageGalleryItemClickEvent = (index, sku, thumbnail)=>{
    const eventLabel = thumbnail.vto ? `${sku} - vto allow` : `${sku} - ${thumbnail.altText} - ${index}`;
    DataLayer.pushEvent({
        event: 'image gallery',
        eventAction: 'click',
        eventLabel
    });
};

const useImageLoaded = (ref)=>{
    const [loaded, setLoaded] = React__default.useState(false);
    const onLoad = ()=>{
        setLoaded(true);
    };
    React__default.useEffect(()=>{
        var ref1;
        if ((ref === null || ref === void 0 ? void 0 : (ref1 = ref.current) === null || ref1 === void 0 ? void 0 : ref1.complete) && ref.current.naturalWidth !== 0) {
            onLoad();
        }
    }, [
        ref
    ]);
    return [
        loaded,
        onLoad
    ];
};

const ImageZoom = ({ children , onZoom  })=>{
    var ref;
    const ref1 = React__default.useRef(null);
    const [zooming, setZooming] = React__default.useState(false);
    const handleMouseMove = (e)=>{
        if (e.target instanceof Element) {
            const { left , top , width , height  } = e.target.getBoundingClientRect();
            const x = Math.round((e.clientX - left) / width * 100);
            const y = Math.round((e.clientY - top) / height * 100);
            // We update the styles using a ref to directly manipulate the DOM
            // This way we we don't have to change state for every mouse movement, which would cause a lot of rerenders
            if (ref1.current) {
                ref1.current.style.transformOrigin = `${x}% ${y}%`;
            }
        }
    };
    const resetZoom = ()=>{
        setZooming(false);
        if (ref1.current) {
            ref1.current.style.transformOrigin = `0% 0%`;
        }
    };
    const enableZoom = ()=>{
        setZooming(true);
        onZoom();
    };
    const transformOrigin = (ref1 === null || ref1 === void 0 ? void 0 : (ref = ref1.current) === null || ref === void 0 ? void 0 : ref.style.transformOrigin) || '0% 0%';
    return /*#__PURE__*/ React__default.createElement("div", {
        onMouseEnter: enableZoom,
        onMouseLeave: resetZoom,
        onMouseMove: handleMouseMove,
        className: classnames('image-zoom', {
            'image-zoom--hover': zooming
        }),
        "data-t": "image-zoom"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "image-zoom__img",
        "data-t": "zoom__image",
        style: {
            transformOrigin
        },
        ref: ref1
    }, children));
};

/**
 * Image sizes below are as defined for the Image Gallery in ZeroHeight.
 * https://zeroheight.com/9qvxm097c/p/476c37-product-image-gallery/b/40f911
 */ const defaultImageSizes$1 = {
    M: 768,
    L: 601,
    XL: 877,
    MAX: 877
};
function zoomHandler(altText, sku) {
    let hasFired = false;
    return function() {
        if (!hasFired) {
            hasFired = true;
            pushImageGalleryZoomEvent(altText, sku);
        }
    };
}
const ProductImage = /*#__PURE__*/ React.forwardRef(({ altText , children , className , imageParams , imageSizes , imageUrl , onClick , showImage =true , sku ='' , zoom =false ,  }, ref)=>{
    const { getLabel  } = useIntl();
    const imageRef = React.useRef(null);
    const [loaded, onLoad] = useImageLoaded(imageRef);
    const imgSizes = zoom && imageSizes ? imageSizes : defaultImageSizes$1;
    const srcSet = Util.createImageSrcSet(imageUrl, imgSizes, imageParams);
    const sizes = Util.createImageSizes(imgSizes);
    const image = /*#__PURE__*/ React.createElement("img", {
        ref: imageRef,
        width: "100%",
        src: imageUrl,
        srcSet: srcSet,
        sizes: sizes,
        alt: altText,
        onLoad: onLoad,
        className: classnames('product-image__img', className, {
            [`${className}__img`]: !!className
        })
    });
    // We don't want to enable the zoom functionality for specific images due to a low resolution.
    const ImageWrapper = ()=>zoom ? /*#__PURE__*/ React.createElement(ImageZoom, {
            onZoom: zoomHandler(altText, sku)
        }, image) : /*#__PURE__*/ React.createElement(React.Fragment, null, image)
    ;
    /**
     * Please note:
     * noscript is used to make sure a reference to the image will be available to google in the document, for SEO purposes, even if the image isn't loaded.
     * For this reason need we handle the showing/not showing of the image in this component.
     */ const content = ()=>/*#__PURE__*/ React.createElement(React.Fragment, null, !loaded && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(SkeletonLoader, null, /*#__PURE__*/ React.createElement("svg", {
            fill: "none",
            xmlns: "http://www.w3.org/2000/svg",
            height: "210",
            "aria-label": getLabel('common.loadingText')
        }, /*#__PURE__*/ React.createElement("rect", {
            width: "100%",
            height: "100%",
            rx: "5",
            fill: "currentColor"
        }))), /*#__PURE__*/ React.createElement("noscript", null, /*#__PURE__*/ React.createElement("img", {
            src: imageUrl,
            alt: altText
        }))), showImage && /*#__PURE__*/ React.createElement(ImageWrapper, null), children)
    ;
    if (onClick) {
        return /*#__PURE__*/ React.createElement("button", {
            ref: ref,
            onClick: onClick,
            className: classnames('product-image', {
                [`${className}__wrapper`]: !!className
            })
        }, content());
    } else {
        return /*#__PURE__*/ React.createElement("div", {
            className: classnames('product-image', {
                [`${className}__wrapper`]: !!className
            })
        }, content());
    }
});

const useImageSlider = (totalImages, { elementIdentifierString ='scroller' , initialIndex =0 , enableKeyEvents =false  })=>{
    const [activeIndex, setActiveIndex] = React__default.useState(initialIndex || 0);
    const interruptSwitchEffect = React__default.useRef(false);
    const setSlide = React__default.useCallback((index)=>{
        if (index !== activeIndex) {
            setActiveIndex(index);
        }
    }, [
        activeIndex
    ]);
    const prevSlide = React__default.useCallback(()=>{
        setActiveIndex((index)=>index !== 0 ? index - 1 : 0
        );
    }, []);
    const nextSlide = React__default.useCallback(()=>{
        setActiveIndex((index)=>index !== totalImages - 1 ? index + 1 : totalImages - 1
        );
    }, [
        totalImages
    ]);
    React__default.useEffect(()=>{
        setActiveIndex(initialIndex || 0);
    }, [
        initialIndex
    ]);
    React__default.useEffect(()=>{
        if (!enableKeyEvents) {
            // Only enable key events when true to prevent conflicts with other keyboard events
            return;
        }
        function handleKeyDownEvent(event) {
            switch(event.key){
                case 'Left':
                case 'ArrowLeft':
                    event.preventDefault();
                    prevSlide();
                    break;
                case 'Right':
                case 'ArrowRight':
                    event.preventDefault();
                    nextSlide();
                    break;
            }
        }
        const target = document;
        if (target) {
            target.addEventListener('keydown', handleKeyDownEvent, false);
            return ()=>target.removeEventListener('keydown', handleKeyDownEvent, false)
            ;
        }
    }, [
        enableKeyEvents,
        nextSlide,
        prevSlide
    ]);
    const handleInView = React__default.useCallback((inView, index)=>{
        if (inView && index !== activeIndex) {
            setSlide(index);
            interruptSwitchEffect.current = true;
        }
    }, [
        activeIndex,
        setSlide
    ]);
    React__default.useEffect(()=>{
        if (!interruptSwitchEffect.current) {
            var ref;
            (ref = document === null || document === void 0 ? void 0 : document.getElementById(`${elementIdentifierString}-${activeIndex}`)) === null || ref === void 0 ? void 0 : ref.scrollIntoView({
                block: 'nearest'
            });
        } else {
            interruptSwitchEffect.current = false;
        }
    }, [
        activeIndex
    ]);
    const onFirstSlide = activeIndex < 1;
    const onLastSlide = activeIndex >= totalImages - 1;
    return {
        setSlide,
        handleInView,
        prevSlide,
        nextSlide,
        onFirstSlide,
        onLastSlide,
        activeIndex,
        interruptSwitchEffect
    };
};

const HorizontalImageSlider = ({ activeImage , images , onClick  })=>{
     return React.createElement("div", {
        className: "horizontal-image-slider"
    }, images.map((image)=>{
         return React.createElement("button", {
            type: "button",
            onClick: ()=>onClick(image)
            ,
            key: `${image.label}-thumb`,
            className: classnames('horizontal-image-slider__button', {
                'horizontal-image-slider__button--active': (activeImage === null || activeImage === void 0 ? void 0 : activeImage.label) === image.label,
                'horizontal-image-slider__button--highlighted': image.highlighted,
                'horizontal-image-slider__button--vto': image.vto
            })
        }, /*#__PURE__*/ React.createElement("div", {
            className: "aspect-wrapper"
        }, /*#__PURE__*/ React.createElement("picture", {
            className: "horizontal-image-slider__picture"
        }, /*#__PURE__*/ React.createElement("source", {
            srcSet: image.thumbnailUrl,
            media: "(max-width: 1024px)"
        }), /*#__PURE__*/ React.createElement("img", {
            src: image.url,
            className: "horizontal-image-slider__image",
            alt: image.altText
        })), image.vto && /*#__PURE__*/ React.createElement("div", {
            className: "horizontal-image-slider-try-on-overlay"
        }, /*#__PURE__*/ React.createElement(Icon, {
            className: "horizontal-image-slider-try-on-overlay__icon",
            name: "try-on"
        }))));
    }));
};

var VtoBanner = (_param)=>{
    var { onClick , vtoBanner  } = _param, productImageProps = swcHelpers.objectWithoutProperties(_param, [
        "onClick",
        "vtoBanner"
    ]);
    const { getLabel  } = useIntl();
    return /*#__PURE__*/ React.createElement("div", {
        className: "vto-banner"
    }, /*#__PURE__*/ React.createElement(ProductImage, swcHelpers.extends({}, productImageProps), /*#__PURE__*/ React.createElement("div", {
        className: "vto-banner__overlay"
    }, /*#__PURE__*/ React.createElement(Heading, {
        component: "h4",
        variant: "smallest",
        className: "vto-banner__title"
    }, vtoBanner.title), (vtoBanner === null || vtoBanner === void 0 ? void 0 : vtoBanner.description) && /*#__PURE__*/ React.createElement(Text, {
        variant: "body",
        className: "vto-banner__sub"
    }, vtoBanner.description), /*#__PURE__*/ React.createElement(Button, {
        caption: getLabel('vto.callToAction'),
        "data-t": "vto-banner-button",
        variant: "outline",
        icon: "try-on",
        color: "primary",
        size: "small",
        className: "vto-banner__button",
        onClick: onClick
    }))));
};

const defaultImageSizes = {
    M: 768,
    L: 1024,
    XL: 1024,
    MAX: 1440
};
const ConditionalWrapper = ({ condition , wrapper , children  })=>condition ? wrapper(children) : children
;
const ZoomModal = ({ isOpen , images , onClose , openingIndex  })=>{
    const { getLabel  } = useIntl();
    const isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    const extendedImages = React.useMemo(()=>{
        return images.map((image)=>swcHelpers.objectSpread({}, image, {
                srcSet: Util.createImageSrcSet(image.url, defaultImageSizes, {
                    fit: 'fill',
                    format: 'jpeg',
                    aspectRatio: 16 / 9
                }),
                sizes: Util.createImageSizes(defaultImageSizes)
            })
        ).filter((image)=>!image.vto
        );
    }, [
        images
    ]);
    const { setSlide , prevSlide , nextSlide , handleInView , onFirstSlide , onLastSlide , activeIndex , interruptSwitchEffect ,  } = useImageSlider(extendedImages.length, {
        initialIndex: openingIndex || 0,
        elementIdentifierString: 'zoom-modal__image-slide',
        enableKeyEvents: isOpen
    });
    const handlePrevClick = React.useCallback((event)=>{
        event.preventDefault();
        prevSlide();
    }, [
        prevSlide
    ]);
    const handleNextClick = React.useCallback((event)=>{
        event.preventDefault();
        nextSlide();
    }, [
        nextSlide
    ]);
    const handleClose = React.useCallback(()=>{
        interruptSwitchEffect.current = true;
        setTimeout(()=>interruptSwitchEffect.current = false
        , 200);
        onClose === null || onClose === void 0 ? void 0 : onClose();
    }, [
        onClose
    ]);
    return /*#__PURE__*/ React.createElement(Modal, {
        isOpen: isOpen,
        showIcon: true,
        isFullScreen: true,
        onModalClose: handleClose,
        className: "zoom-modal"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "zoom-modal__image-container"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "zoom-modal__image-wrapper"
    }, /*#__PURE__*/ React.createElement(ConditionalWrapper, {
        condition: isSmallScreen,
        wrapper: (children)=>/*#__PURE__*/ React.createElement(TransformWrapper, {
                panning: {
                    disabled: true
                }
            }, children)
    }, extendedImages.map((image, index)=>/*#__PURE__*/ React.createElement(InView, {
            key: image.label,
            threshold: 0.8,
            onChange: (inView)=>handleInView(inView, index)
        }, ({ ref  })=>isSmallScreen ? /*#__PURE__*/ React.createElement(TransformComponent, {
                wrapperClass: "zoom-modal__zoom-wrapper"
            }, /*#__PURE__*/ React.createElement("picture", {
                ref: ref,
                "data-index": index,
                id: `zoom-modal__image-slide-${index}`,
                className: "zoom-modal__picture"
            }, /*#__PURE__*/ React.createElement("img", {
                src: image.url,
                className: "zoom-modal__image",
                srcSet: image.srcSet,
                sizes: image.sizes,
                alt: image.altText
            }))) : /*#__PURE__*/ React.createElement("picture", {
                ref: ref,
                "data-index": index,
                id: `zoom-modal__image-slide-${index}`,
                className: "zoom-modal__picture"
            }, /*#__PURE__*/ React.createElement("img", {
                src: image.url,
                className: "zoom-modal__image",
                srcSet: image.srcSet,
                sizes: image.sizes,
                alt: image.altText
            }))
        )
    )))), /*#__PURE__*/ React.createElement("div", {
        className: "zoom-modal__buttons"
    }, /*#__PURE__*/ React.createElement(IconButton, {
        className: classnames('zoom-modal__chevron', {
            disabled: onFirstSlide
        }),
        disabled: onFirstSlide,
        onClick: handlePrevClick,
        "aria-label": getLabel('common.actions.previous')
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "chevron-left"
    })), /*#__PURE__*/ React.createElement(IconButton, {
        className: classnames('zoom-modal__chevron', {
            disabled: onLastSlide
        }),
        disabled: onLastSlide,
        onClick: handleNextClick,
        "aria-label": getLabel('common.actions.next')
    }, /*#__PURE__*/ React.createElement(Icon, {
        name: "chevron-right"
    }))), /*#__PURE__*/ React.createElement("div", {
        className: "zoom-modal__navigator",
        "data-t": "zoom-modal__navigator"
    }, extendedImages.map((image, index)=>/*#__PURE__*/ React.createElement("button", {
            key: image.label,
            "data-t": `zoom-modal__navigator-${index}`,
            className: classnames('zoom-modal__navigator-button', {
                _is_active: index === activeIndex
            }),
            onClick: ()=>setSlide(index)
        })
    )));
};

const ProductImageGalleryMobile = ({ images , imageParams , isVtoEnabled , setVtoActive , vtoActive , sku , vtoBanner , handleImageInteraction , zoomModalOpenIndex ,  })=>{
    const { setSlide , activeIndex , handleInView  } = useImageSlider(images.length, {
        elementIdentifierString: 'mobile__image-scroll-item'
    });
    const activeImage1 = images[activeIndex];
    // when vto is activated make sure the gallery switches to the vto slide
    React.useEffect(()=>{
        if (vtoActive && !activeImage1.vto) {
            setSlide(images.findIndex((image)=>image.vto
            ));
        }
    }, [
        images,
        vtoActive
    ]);
    const handleThumbnailClick = React.useCallback((thumbnail)=>{
        const activeImage = images.findIndex((image)=>image.label === thumbnail.label
        );
        setSlide(activeImage);
        sku && pushImageGalleryItemClickEvent(activeImage, sku, thumbnail);
    }, [
        images,
        setSlide
    ]);
    if (!Boolean(images.length)) {
        return null;
    }
    return /*#__PURE__*/ React.createElement("section", {
        className: "product-image-gallery-mobile"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "product-image-gallery-mobile__images"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "product-image-gallery-mobile__wrapper"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "product-image-gallery-mobile__image-scroller"
    }, images.map((image, index)=>/*#__PURE__*/ React.createElement(InView, {
            key: image.label,
            threshold: 0.8,
            onChange: (inView)=>handleInView(inView, index)
        }, ({ ref  })=>/*#__PURE__*/ React.createElement("div", {
                ref: ref,
                id: `mobile__image-scroll-item-${index}`,
                className: `product-image-gallery-mobile__image-scroller-item`
            }, image.vto && isVtoEnabled && !vtoActive && vtoBanner && /*#__PURE__*/ React.createElement(VtoBanner, {
                altText: image.altText,
                imageUrl: image.url,
                imageParams: imageParams,
                vtoBanner: vtoBanner,
                onClick: ()=>setVtoActive(true)
            }), image.vto && vtoActive && isVtoEnabled && !!sku && /*#__PURE__*/ React.createElement(DittoContainer, {
                sku: sku,
                vtoBanner: vtoBanner,
                onClose: ()=>setVtoActive(false)
            }), !image.vto && /*#__PURE__*/ React.createElement(ProductImage, {
                altText: image.altText,
                imageUrl: image.url,
                imageParams: imageParams,
                onClick: ()=>handleImageInteraction(index)
            }))
        )
    ))), images.length > 1 && /*#__PURE__*/ React.createElement("div", {
        className: "product-image-gallery-mobile__thumbnails",
        "data-t": "image-gallery-mobile__thumbnails"
    }, /*#__PURE__*/ React.createElement(HorizontalImageSlider, {
        images: images,
        onClick: handleThumbnailClick,
        activeImage: activeImage1
    }))), /*#__PURE__*/ React.createElement(ZoomModal, {
        isOpen: zoomModalOpenIndex !== null,
        openingIndex: activeIndex,
        onClose: ()=>handleImageInteraction(null)
        ,
        images: images
    }));
};

const desktopImageSizes = {
    M: 768,
    L: 1024,
    XL: 1440,
    MAX: 1440
};
const ProductImageGallery = ({ initialImages , isVtoEnabled , expandableImages , showMoreLabel , showLessLabel , handleGalleryExpansion , handleImageInteraction , isImageVisible , setVtoActive , vtoActive , zoomModalOpenIndex , buttonClickedForZoomRef , vtoBanner , sku ,  })=>{
    if (!initialImages || initialImages.length === 0) {
        return null;
    }
    const handleImageClick = (index, altText)=>{
        handleImageInteraction(index);
        // push dataLayer event only for the first image
        if (sku && index === 0) {
            pushImageGalleryOpenEvent(altText, index, sku);
        }
    };
    return /*#__PURE__*/ React.createElement("section", {
        "data-t": "product-image-gallery",
        className: classnames('product-image-gallery', {
            'product-image-gallery--full-width': initialImages.length <= 2
        })
    }, /*#__PURE__*/ React.createElement("div", {
        className: "product-image-gallery__images"
    }, initialImages.map((image, index)=>/*#__PURE__*/ React.createElement("div", {
            key: image.label,
            className: classnames('product-image-gallery__image', {
                'product-image-gallery__image--highlighted': image.highlighted
            })
        }, image.vto && isVtoEnabled && !vtoActive && vtoBanner && /*#__PURE__*/ React.createElement(VtoBanner, {
            vtoBanner: vtoBanner,
            altText: image.altText,
            imageUrl: image.url,
            onClick: ()=>setVtoActive(true)
        }), image.vto && vtoActive && isVtoEnabled && !!sku && /*#__PURE__*/ React.createElement("div", {
            className: "product-image-gallery__vto product-image"
        }, /*#__PURE__*/ React.createElement(DittoContainer, {
            sku: sku,
            onClose: ()=>setVtoActive(false)
            ,
            vtoBanner: vtoBanner
        })), !image.vto && /*#__PURE__*/ React.createElement(ProductImage, {
            ref: index === zoomModalOpenIndex ? buttonClickedForZoomRef : null,
            onClick: ()=>handleImageClick(index, image.altText)
            ,
            altText: image.altText,
            imageUrl: image.url,
            zoom: image.zoom,
            imageSizes: desktopImageSizes,
            sku: sku
        }))
    )), !!expandableImages.length && /*#__PURE__*/ React.createElement(Expander, {
        className: "product-image-gallery__expanded-images",
        showMoreLabel: showMoreLabel,
        showLessLabel: showLessLabel,
        id: "gallery-expander",
        onExpandCallback: handleGalleryExpansion
    }, expandableImages.map((image, index)=>/*#__PURE__*/ React.createElement("div", {
            key: image.label,
            className: classnames('product-image-gallery__expanded-image', {
                'product-image-gallery__image--highlighted': image.highlighted
            })
        }, !!image.vto && vtoBanner && /*#__PURE__*/ React.createElement(VtoBanner, {
            vtoBanner: vtoBanner,
            altText: image.altText,
            imageUrl: image.url,
            onClick: ()=>setVtoActive(true)
            ,
            showImage: isImageVisible
        }), !image.vto && /*#__PURE__*/ React.createElement(ProductImage, {
            ref: index + initialImages.length === zoomModalOpenIndex ? buttonClickedForZoomRef : null,
            onClick: ()=>handleImageClick(index + initialImages.length, image.altText)
            ,
            altText: image.altText,
            imageUrl: image.url,
            showImage: isImageVisible,
            imageSizes: desktopImageSizes,
            zoom: image.zoom,
            sku: sku
        }))
    )), /*#__PURE__*/ React.createElement(ZoomModal, {
        isOpen: zoomModalOpenIndex !== null,
        openingIndex: zoomModalOpenIndex,
        onClose: ()=>handleImageInteraction(null)
        ,
        images: [
            ...initialImages,
            ...expandableImages
        ]
    }));
};

const createAltTag = ({ variant , prefix  })=>{
    return [
        prefix,
        variant.displayName
    ].filter(Boolean).filter((entry)=>entry != ''
    ).join(' ');
};

const defaultImageParams = {
    fit: 'fill',
    format: 'png',
    aspectRatio: 16 / 9
};
const highlightedPackshots = new Set([
    'On_Model_Female01',
    'On_Model_Female02',
    'On_Model_Female03',
    'On_Model_Male01',
    'On_Model_Male02',
    'On_Model_Male03', 
]);
const ProductImageGalleryContainer = ()=>{
    const isSmallScreen = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    const { getLabel  } = useIntl();
    const [isImageVisible, setImageVisible] = React.useState(false);
    const [zoomMobileOpenIndex, setZoomMobileOpenIndex] = React.useState(null);
    const buttonClickedForZoomRef = React.useRef(null);
    const [isExpanded, setIsExpanded] = React.useState(false);
    const { activeVariant , product: { isOfferedWithPrescriptions  } ,  } = useProductData();
    const { vtoBanner  } = useCmsData();
    const { isVtoActive , isVtoFeatureEnabled , setVtoActive  } = useVto();
    const handleGalleryExpansion = React.useCallback((expanded)=>{
        if (!isImageVisible && expanded) {
            setImageVisible(true);
        }
        setIsExpanded(expanded);
    }, [
        isImageVisible
    ]);
    React.useEffect(()=>{
        if (isImageVisible) {
            pushImageGalleryItemExpandedEvent(isExpanded);
        }
    }, [
        isImageVisible,
        isExpanded
    ]);
    const handleImageInteraction = React.useCallback((index)=>{
        if (index === null) {
            var ref;
            (ref = buttonClickedForZoomRef.current) === null || ref === void 0 ? void 0 : ref.focus();
        }
        setZoomMobileOpenIndex(index);
    }, []);
    const { initialImages: initialImages1 , expandableImages: expandableImages1 , enhancedImages: enhancedImages1  } = React.useMemo(()=>{
        var ref;
        // Use placeholder if there are no images
        const variantImages = ((ref = activeVariant.media.images) === null || ref === void 0 ? void 0 : ref.length) > 0 ? activeVariant.media.images : [
            placeholderImage
        ];
        var _label, _label1, _label2;
        const enhancedImages = variantImages.map((image)=>swcHelpers.objectSpread({}, image, {
                label: (_label = image.label) !== null && _label !== void 0 ? _label : '',
                thumbnailUrl: createImageUrl(image.url, swcHelpers.objectSpread({}, defaultImageParams, {
                    width: 224
                })),
                altText: createAltTag({
                    variant: activeVariant,
                    prefix: image.label ? getLabel(`products.image.${image.label.toLowerCase()}`) : undefined
                }),
                highlighted: highlightedPackshots.has((_label1 = image.label) !== null && _label1 !== void 0 ? _label1 : ''),
                zoom: !highlightedPackshots.has((_label2 = image.label) !== null && _label2 !== void 0 ? _label2 : '')
            })
        );
        const vtoImg = vtoBanner ? {
            url: vtoBanner.image.url,
            dimensions: {
                w: vtoBanner.image.original.width,
                h: vtoBanner.image.original.height
            },
            thumbnailUrl: createImageUrl(vtoBanner.image.url, swcHelpers.objectSpread({}, defaultImageParams, {
                width: 112,
                height: 63,
                fit: 'cover'
            })),
            altText: vtoBanner.image.description || vtoBanner.title,
            highlighted: false,
            zoom: false,
            label: 'vto',
            vto: true
        } : swcHelpers.objectSpread({}, enhancedImages[0], {
            label: 'vto',
            zoom: false,
            vto: true
        });
        if (isVtoActive && !isSmallScreen && activeVariant.isVtoEnabled) {
            enhancedImages.unshift(vtoImg);
        } else if (isVtoActive && activeVariant.isVtoEnabled) {
            enhancedImages.push(vtoImg);
        } else if (vtoBanner && isVtoFeatureEnabled && activeVariant.isVtoEnabled) {
            enhancedImages.push(vtoImg);
        }
        /**
     * In order to correctly follow the design we need to decide how many images we will initially render on load.
     * If there is a highlightedPackshot we have more space to render an extra image.
     *
     * This follows the following logic:
     * 1. If there are no highlightedPackshots: render 3 images.
     * 2. If only the first image is a highlightedPackshot: render 3 images.
     * 3. If the second OR third image is a highlightedPackshot: render 4 images.
     * 4. If the second AND third images are highlightedPackshots: render 3 images.
     */ const initialImageSize = enhancedImages.slice(1, 3).filter((image)=>image.highlighted === true
        ).length === 1 ? 4 : 3;
        const initialImages = enhancedImages.slice(0, initialImageSize);
        const expandableImages = enhancedImages.slice(initialImageSize);
        return {
            initialImages,
            expandableImages,
            enhancedImages
        };
    }, [
        activeVariant,
        vtoBanner,
        isVtoActive,
        isVtoFeatureEnabled,
        isSmallScreen,
        getLabel
    ]);
    useEffect(()=>{
        if (activeVariant.sku && activeVariant.isVtoEnabled) {
            vtoEvent('vto.view', {
                sku: activeVariant.sku
            });
        }
    }, [
        activeVariant.sku,
        activeVariant.isVtoEnabled
    ]);
    return /*#__PURE__*/ React.createElement(React.Fragment, null, isOfferedWithPrescriptions && /*#__PURE__*/ React.createElement(PrescriptionAvailabilityIndicator, {
        className: "product-image-gallery__rx-indicator"
    }), isSmallScreen ? /*#__PURE__*/ React.createElement(ProductImageGalleryMobile, {
        images: enhancedImages1,
        isVtoEnabled: isVtoFeatureEnabled,
        sku: activeVariant.sku,
        imageParams: defaultImageParams,
        vtoBanner: vtoBanner,
        handleImageInteraction: handleImageInteraction,
        vtoActive: isVtoActive,
        setVtoActive: setVtoActive,
        zoomModalOpenIndex: zoomMobileOpenIndex
    }) : /*#__PURE__*/ React.createElement(ProductImageGallery, {
        initialImages: initialImages1,
        isVtoEnabled: isVtoFeatureEnabled,
        expandableImages: expandableImages1,
        showMoreLabel: getLabel('products.imageGallery.showMore'),
        showLessLabel: getLabel('products.imageGallery.showLess'),
        isImageVisible: isImageVisible,
        handleGalleryExpansion: handleGalleryExpansion,
        handleImageInteraction: handleImageInteraction,
        setVtoActive: setVtoActive,
        sku: activeVariant.sku,
        vtoActive: isVtoActive,
        vtoBanner: vtoBanner,
        zoomModalOpenIndex: zoomMobileOpenIndex,
        buttonClickedForZoomRef: buttonClickedForZoomRef
    }));
};

const getPriceSpecificationConfig = (prices, vat)=>{
    var ref, ref1, ref2, ref3, ref4, ref5, ref6, ref7;
    const regularPrice = prices.originalPrice.amount;
    var ref8;
    const discountedPrice = (ref8 = (ref = prices.discountedPrice) === null || ref === void 0 ? void 0 : (ref1 = ref.price) === null || ref1 === void 0 ? void 0 : ref1.amount) !== null && ref8 !== void 0 ? ref8 : 0;
    const currency = prices.originalPrice.currencyCode;
    const price = discountedPrice > 0 ? discountedPrice : regularPrice;
    const vatRate = vat ? vat : 0;
    const validFrom = (ref2 = prices.discountedPrice) === null || ref2 === void 0 ? void 0 : (ref3 = ref2.discountInformation) === null || ref3 === void 0 ? void 0 : (ref4 = ref3.validity) === null || ref4 === void 0 ? void 0 : ref4.validFrom;
    const validThrough = (ref5 = prices.discountedPrice) === null || ref5 === void 0 ? void 0 : (ref6 = ref5.discountInformation) === null || ref6 === void 0 ? void 0 : (ref7 = ref6.validity) === null || ref7 === void 0 ? void 0 : ref7.validUntil;
    return {
        price,
        vatRate,
        currency,
        validFrom,
        validThrough
    };
};

const ProductStructuredData = ({ url  })=>{
    var ref;
    const { activeVariant , product  } = useProductData();
    const { brandName , category , name , taxCategory , ratingsSummary  } = product;
    const { description , seoConfig , price , slug , sku , media: { images  } ,  } = activeVariant;
    const vat = taxCategory === null || taxCategory === void 0 ? void 0 : (ref = taxCategory.rate) === null || ref === void 0 ? void 0 : ref.amount;
    const priceSpecification = getPriceSpecificationConfig(price, vat);
    const img = images[0] ? images[0].url : seoConfig.ogImageUrl;
    const productUrl = useLink({
        route: 'products.detail',
        params: {
            slug,
            sku,
            key: 'sunglasses'
        }
    });
    const fullUrl = `${url}${productUrl === null || productUrl === void 0 ? void 0 : productUrl.href}`;
    return /*#__PURE__*/ React.createElement(Product, {
        name: name || seoConfig.metaTitle,
        image: img,
        description: description,
        brand: brandName,
        category: category,
        sku: sku,
        url: fullUrl,
        ratingsSummary: ratingsSummary,
        priceSpecificationConfig: priceSpecification,
        isCentAmount: false
    });
};

const PromoteInstallmentsModalSlot = ({ siteLogos , price , numberOfInstallments  })=>{
    const { getLabel , currency , locale  } = useIntl();
    const monthsIndexArray = useMemo(()=>{
        return Array(numberOfInstallments - 1).fill(0).map((_value, index)=>index + 1
        );
    }, [
        numberOfInstallments
    ]);
    if (numberOfInstallments <= 1) {
        return null;
    }
    const logo = /*#__PURE__*/ React__default.createElement("img", {
        src: siteLogos.payInstallmentsLogo,
        alt: getLabel('installments.modal.logoAlt')
    });
    const pricePerMonthValue = Math.floor(price / numberOfInstallments);
    // First month can be slightly different due to rounding errors. Make sure it all adds up to the total price
    const priceNow = formatPrice({
        locale,
        value: price - (numberOfInstallments - 1) * pricePerMonthValue,
        currency,
        isCentAmount: true
    });
    const pricePerMonth = formatPrice({
        locale,
        value: pricePerMonthValue,
        currency,
        isCentAmount: true
    });
    return /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__intro"
    }, /*#__PURE__*/ React__default.createElement("p", null, getLabel('installments.modal.partnerDescription'), " ", logo)), /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__steps-wrapper"
    }, /*#__PURE__*/ React__default.createElement("p", null, getLabel('installments.modal.stepsTitle')), /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__steps"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__step"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__step-number"
    }, "1"), /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__step-description"
    }, getLabel('installments.modal.step1'))), /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__step"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__step-number"
    }, "2"), /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__step-description"
    }, getLabel('installments.modal.step2')), logo && /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__step-logo"
    }, logo)), /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__step"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__step-number"
    }, "3"), /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__step-description"
    }, getLabel('installments.modal.step3'))), /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__step"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__step-number"
    }, "4"), /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__step-description"
    }, getLabel('installments.modal.step4'))))), /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__pay"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "promote-installments-modal-slot__pay-now"
    }, /*#__PURE__*/ React__default.createElement("div", null, getLabel('installments.modal.payNow')), /*#__PURE__*/ React__default.createElement("div", null, priceNow, " ", getLabel('installments.modal.payNowMethod')), /*#__PURE__*/ React__default.createElement("br", null), /*#__PURE__*/ React__default.createElement("div", null, getLabel('installments.modal.payNowMethodDescription'))), /*#__PURE__*/ React__default.createElement("dl", {
        className: "promote-installments-modal-slot__pay-summary"
    }, /*#__PURE__*/ React__default.createElement("dt", null, getLabel('installments.modal.summaryTitle')), /*#__PURE__*/ React__default.createElement("dd", null), /*#__PURE__*/ React__default.createElement("dt", null, getLabel('installments.modal.summaryDescriptionNow')), /*#__PURE__*/ React__default.createElement("dd", null, priceNow), monthsIndexArray.map((index)=>/*#__PURE__*/ React__default.createElement(React__default.Fragment, {
            key: index
        }, /*#__PURE__*/ React__default.createElement("dt", null, getLabel(`installments.modal.summaryDescription${index}`)), /*#__PURE__*/ React__default.createElement("dd", null, pricePerMonth))
    ))));
};

// when enabledProduct is undefined this component will render for every product type
// when enabledProduct is set e.g. ['sunglasses'] it will render ONLY for this product type
const getExcludedProductType = (enabledProductTypes, productType)=>{
    if (!enabledProductTypes || !productType) {
        return false;
    }
    return !enabledProductTypes.some((product)=>product.toLowerCase() === productType.toLowerCase()
    );
};
const PromoteInstallments = ({ siteLogos , numberOfInstallments , minimumPrice , productType , enabledProductTypes , textAlign  })=>{
    var ref;
    const { currency , locale  } = useIntl();
    const { activeVariant  } = useProductData();
    const { price  } = activeVariant;
    const { amount: priceAmount  } = ((ref = price.discountedPrice) === null || ref === void 0 ? void 0 : ref.price) || price.originalPrice;
    const excludedProductType = getExcludedProductType(enabledProductTypes, productType);
    const installmentPrice = formatPrice({
        locale,
        value: priceAmount / numberOfInstallments,
        currency,
        isCentAmount: false
    });
    // conditions to render component
    if (priceAmount < minimumPrice || excludedProductType) {
        return null;
    }
    return /*#__PURE__*/ React__default.createElement(PromoteInstallmentsLabel, {
        installmentPrice: installmentPrice,
        numberOfInstallments: numberOfInstallments,
        textAlign: textAlign,
        modalBodySlot: /*#__PURE__*/ React__default.createElement(PromoteInstallmentsModalSlot, {
            siteLogos: siteLogos,
            price: priceAmount,
            numberOfInstallments: numberOfInstallments
        })
    });
};

const StickyMobileCtaAndPrice = ({ cta , className  })=>{
    var ref;
    const { activeVariant: { price  } ,  } = useProductData();
    let priceValue = price.originalPrice.amount;
    let priceType = 'default';
    const discountedValue = (ref = price.discountedPrice) === null || ref === void 0 ? void 0 : ref.price.amount;
    if (discountedValue) {
        priceValue = discountedValue;
        priceType = 'discount';
    }
    return /*#__PURE__*/ React.createElement(StickyCallToAction, {
        className: className,
        button: cta,
        price: /*#__PURE__*/ React.createElement(Price, {
            value: priceValue,
            currency: price.originalPrice.currencyCode,
            centAmount: false,
            type: priceType
        })
    });
};

const UnpublishedProductEcommContent = ({ productColor  })=>{
    const { getLabel  } = useIntl();
    return /*#__PURE__*/ React.createElement("section", {
        className: "unpublished-product-ecomm-content"
    }, productColor && /*#__PURE__*/ React.createElement(React.Fragment, null, /*#__PURE__*/ React.createElement(Hr, null), /*#__PURE__*/ React.createElement(Text, {
        variant: "body",
        color: "text-secondary",
        "data-t": "productColor-text",
        className: "unpublished-product-ecomm-content__product-color"
    }, productColor)), /*#__PURE__*/ React.createElement(Hr, null), /*#__PURE__*/ React.createElement(Alert, {
        type: "info",
        icon: "info",
        message: getLabel('products.unpublished.productUnavailable')
    }));
};

const UnpublishedProductEcommContentContainer = ()=>{
    const { activeVariant  } = useProductData();
    const productColor = activeVariant.facets.color;
    return /*#__PURE__*/ React.createElement(UnpublishedProductEcommContent, {
        productColor: productColor
    });
};

const UnpublishedProductImage = ({ src , alt  })=>/*#__PURE__*/ React.createElement("img", {
        className: "unpublished-product-image",
        width: "100%",
        src: src,
        alt: alt
    })
;

const DEFAULT_SUFFIX = '?q=70&w=1200&h=630&fit=fill&fm=jpeg';
const THUMBNAIL_SUFFIX = '?w=64&fm=jpeg&auto=format';
const getProductImageUrl = (media, suffix)=>{
    var ref;
    if (!(media === null || media === void 0 ? void 0 : media.images) || !(media === null || media === void 0 ? void 0 : media.images.length)) {
        return `${placeholderImage.url}${suffix !== null && suffix !== void 0 ? suffix : DEFAULT_SUFFIX}`;
    }
    // The label 'Front' comes from Bynder Images
    return `${(ref = media === null || media === void 0 ? void 0 : media.images.filter((image)=>image.label === 'Front'
    )[0]) === null || ref === void 0 ? void 0 : ref.url}${suffix !== null && suffix !== void 0 ? suffix : DEFAULT_SUFFIX}`;
};
const getVariantItemImageUrl = (media)=>getProductImageUrl(media, THUMBNAIL_SUFFIX)
;

const consolidateParentFacets = (variants)=>{
    const parentFacetsMap = new Map();
    variants.forEach((variant)=>{
        const { facets , sku , slug , media , displayName  } = variant;
        if (!facets.color) {
            return;
        }
        const parentFacetItem = parentFacetsMap.get(facets.color) || {
            facetTerm: facets.color,
            type: 'color',
            slug,
            name: displayName,
            imageUrl: getVariantItemImageUrl(media),
            childFacets: [],
            sku,
            skuCollection: []
        };
        if (!!facets.size) {
            const newChildFacet = {
                facetTerm: facets.size,
                type: 'size',
                sku,
                slug,
                name: displayName
            };
            parentFacetItem.childFacets.push(newChildFacet);
        }
        parentFacetItem.skuCollection.push(sku);
        parentFacetsMap.set(facets.color, parentFacetItem);
    });
    const facetsMap = Object.fromEntries(parentFacetsMap);
    const parentFacets = Object.values(facetsMap);
    return parentFacets;
};

// This function maps from the Graph variant type to our Client-side ProductFeature[] type
const getProductFeatures = (serverVariant)=>{
    if (serverVariant.__typename !== 'SunglassesVariant') {
        console.error(`The ProductFeature formatter has not yet been implemented for product type ${serverVariant.__typename}`);
        return [];
    }
    const { isGlazeble , isPolarized , uvRaysProtection  } = serverVariant;
    const productFeatures = [];
    if (!!uvRaysProtection) {
        productFeatures.push({
            type: 'uvCategory',
            iconName: 'uv'
        });
    }
    if (!!isPolarized) {
        productFeatures.push({
            type: 'polarized',
            iconName: 'polarized'
        });
    }
    if (!!isGlazeble) {
        productFeatures.push({
            type: 'glazeable',
            iconName: 'reading'
        });
    }
    return productFeatures;
};

var getProductMedia = (serverMedia)=>{
    const images = serverMedia.images.map((_param)=>{
        var { url , label , dimensions: { __typename  }  } = _param, dimensionsRest = swcHelpers.objectWithoutProperties(_param.dimensions, [
            "__typename"
        ]);
        return {
            url,
            label: label !== null && label !== void 0 ? label : undefined,
            dimensions: dimensionsRest
        };
    });
    return {
        images
    };
};

var getProductPrice = (serverVariant)=>{
    const { prices: serverPrices  } = serverVariant;
    if (!serverPrices || !serverPrices.originalPrice) {
        return undefined;
    }
    const { originalPrice: serverOriginalPrice  } = serverPrices;
    const originalPrice = swcHelpers.objectWithoutProperties(serverOriginalPrice, [
        "__typename"
    ]);
    var _discountedPrice;
    const { discountInformation: serverDiscountInfo , price: serverDiscountPrice  } = (_discountedPrice = serverPrices.discountedPrice) !== null && _discountedPrice !== void 0 ? _discountedPrice : {};
    let discountedPrice;
    // Check that the discount information is actually defined
    if (!isNil(serverDiscountInfo) && !isNil(serverDiscountPrice) && !!serverDiscountPrice.currencyCode) {
        // strip out the "__typename" fields that are returned from the BE
        const { __typename: _ , name , description  } = serverDiscountInfo, discountInformation = swcHelpers.objectWithoutProperties(serverDiscountInfo, [
            "__typename",
            "name",
            "description"
        ]);
        const nameInfo = name.map((n)=>{
            return {
                value: n === null || n === void 0 ? void 0 : n.value,
                locale: n === null || n === void 0 ? void 0 : n.locale
            };
        });
        const descriptionInfo = description.map((n)=>{
            return {
                value: n === null || n === void 0 ? void 0 : n.value,
                locale: n === null || n === void 0 ? void 0 : n.locale
            };
        });
        const price = swcHelpers.objectWithoutProperties(serverDiscountPrice, [
            "__typename"
        ]);
        discountedPrice = {
            discountInformation: swcHelpers.objectSpread({
                name: nameInfo,
                description: descriptionInfo
            }, discountInformation),
            price
        } // TODO: Remove this cast once the BE type is updated to make the child attributes mandatory
        ;
    }
    return {
        originalPrice,
        discountedPrice
    };
};

const getMetaTitle = (variant, product)=>[
        variant.metaTitle,
        product.metaTitle,
        variant.displayName
    ].find(isString)
;
const getMetaDescription = (variant, product)=>[
        variant.metaDescription,
        product.metaDescription,
        variant.description,
        product.description
    ].find(isString)
;

// Convert from the Graph key to the CommerceTools key
const specificationDictionary = {
    accessoryColor: 'accessoryColor',
    articleNumber: 'articleNumber',
    brand: 'brand',
    erpId: 'erpId',
    faceShape: 'faceShape',
    frameColor: 'frameColor',
    frameMaterial: 'frameMaterial',
    frameShape: 'frameShape',
    frameType: 'frameType',
    frameSize: 'frameSize',
    isGlazeble: 'glazeble',
    lensColor: 'lensColor',
    material: 'material',
    opcoBackendId: 'opcoBackendId',
    isPolarized: 'polarized',
    prescriptionGlass: 'prescriptionGlass',
    supplierEAN: 'supplierEAN',
    targetGender: 'gender',
    templeType: 'templeType',
    trend: 'trend',
    segment: 'segment'
};
// This function maps from the Graph variant type to our Client-side SpecificationItem[] type
const getProductSpecifications = (serverVariant, brand)=>{
    var ref;
    if (serverVariant.__typename !== 'SunglassesVariant') {
        console.error(`The ProductSpecification formatter has not yet been implemented for product type ${serverVariant.__typename}`);
        return [];
    }
    var ref1;
    const faceShape1 = (ref1 = serverVariant === null || serverVariant === void 0 ? void 0 : (ref = serverVariant.faceShape) === null || ref === void 0 ? void 0 : ref.map((faceShape)=>{
        const shape = String(faceShape.shape);
        return shape.charAt(0).toUpperCase() + shape.slice(1).toLowerCase();
    })) !== null && ref1 !== void 0 ? ref1 : '';
    const { segment , targetGender , frameShape , lensColor , frameColor , erpId , frameMaterial , frameType , frameSize , isGlazeble , opcoBackendId , isPolarized , templeType , trend ,  } = serverVariant;
    const serverSpecifications = Object.entries({
        segment,
        brand,
        targetGender,
        faceShape: faceShape1,
        frameShape,
        lensColor,
        frameColor,
        erpId,
        frameMaterial,
        frameType,
        frameSize,
        isGlazeble,
        opcoBackendId,
        isPolarized,
        templeType,
        trend
    });
    const clientSpecifications = serverSpecifications.map(([graphKey, value])=>{
        const specificationAttribute = specificationDictionary[graphKey];
        if (value === null || value === undefined || !specificationAttribute) {
            return undefined;
        }
        if (isString(value) && value !== '' || isBoolean(value)) {
            return {
                specificationAttribute,
                value
            };
        }
        if (Array.isArray(value) && value.length > 0) {
            return {
                specificationAttribute,
                value
            };
        }
        if (typeof value === 'object' && 'displayName' in value && !!value.displayName) {
            return {
                specificationAttribute,
                value: value.displayName
            };
        }
        return undefined;
    });
    return clientSpecifications.filter(isDefined);
};

const getProductTags = (serverTags)=>{
    if (!serverTags || !serverTags.length) {
        return;
    }
    var _key;
    return serverTags.map((serverTag)=>({
            key: (_key = serverTag.key) !== null && _key !== void 0 ? _key : undefined,
            value: serverTag.value
        })
    );
};

/**
 *
 * As more product types are supported on the Graph, be sure to add them to the mapping here
 */ const getProductType = (serverProductType)=>{
    switch(serverProductType){
        case 'SUNGLASSES':
            return 'Sunglasses';
        default:
            console.error(`Unknown or unsupported productType: ${serverProductType}`);
            return 'Sunglasses';
    }
};

const UnpublishedProductImageContainer = ()=>{
    const { activeVariant: { displayName , media  } ,  } = useProductData();
    const imageUrl = getProductImageUrl(media);
    return /*#__PURE__*/ React.createElement(UnpublishedProductImage, {
        src: imageUrl,
        alt: displayName
    });
};

const VariantPicker = ({ variantItems , activeVariantSku , activeVariantLabel: activeVariantLabelProp , stockCount , isAllowBackOrder , enableStockIndicator , channelAvailability , formatProductUrl , onPick  })=>{
    var ref;
    const { getFormattedLabel , getLabel  } = useIntl();
    const activeVariantLabel = activeVariantLabelProp !== null && activeVariantLabelProp !== void 0 ? activeVariantLabelProp : (ref = variantItems.find((v)=>v.sku === activeVariantSku
    )) === null || ref === void 0 ? void 0 : ref.description;
    const queryString = typeof window !== 'undefined' ? window.location.search : '';
    const labels = {
        title: getFormattedLabel('products.numberOfVariants', {
            labelData: {
                number: variantItems.length
            }
        }),
        screenreaderDefault: getLabel('products.variant.description.screenreader.fallback')
    };
    const stockIndicator = enableStockIndicator ? /*#__PURE__*/ React__default.createElement(StockIndicator, {
        stockCount: stockCount,
        allowBackOrder: isAllowBackOrder,
        channelAvailability: channelAvailability
    }) : null;
    if (!variantItems.length) {
        return null;
    }
    if (variantItems.length === 1) {
        return /*#__PURE__*/ React__default.createElement("div", {
            className: "variant-picker"
        }, /*#__PURE__*/ React__default.createElement("div", {
            className: "variant-picker__header"
        }, activeVariantLabel && /*#__PURE__*/ React__default.createElement("div", {
            className: "variant-picker__info"
        }, activeVariantLabel), stockIndicator));
    }
    return /*#__PURE__*/ React__default.createElement("div", {
        className: "variant-picker"
    }, /*#__PURE__*/ React__default.createElement("div", {
        className: "variant-picker__header"
    }, /*#__PURE__*/ React__default.createElement("strong", {
        id: "variants-label"
    }, labels.title), stockIndicator), /*#__PURE__*/ React__default.createElement("div", {
        className: "variant-picker__info"
    }, activeVariantLabel), /*#__PURE__*/ React__default.createElement("div", {
        className: "variant-picker__variants",
        role: "radiogroup"
    }, variantItems.map((variant)=>{
        const { sku , imageUrl , slug , description , productType  } = variant;
        const accessibleName = description || `${sku} - ${labels.screenreaderDefault}`;
        const variantUrl = formatProductUrl({
            sku,
            slug,
            productType
        });
        const href = `${variantUrl}${queryString}`;
        return /*#__PURE__*/ React__default.createElement(SelectionBoxRadio, {
            // Only enable tab stop on the active variant
            // Arrow keys can be used to move between the different radio buttons
            tabIndex: activeVariantSku === sku ? 0 : -1,
            "aria-labelledby": "variants-label",
            checked: activeVariantSku === sku ? true : false,
            key: sku,
            title: accessibleName,
            name: "variant",
            className: classnames('variant-picker__variant', {
                'variant-picker__variant--active': activeVariantSku === sku
            }),
            onChange: ()=>onPick(variant, href)
        }, /*#__PURE__*/ React__default.createElement("a", {
            href: href,
            "data-t": "variant-picker-button",
            // user will tab-stop on the radio buttons of the SelectionBoxRadio
            tabIndex: -1,
            onClick: (event)=>onPick(variant, href, event)
        }, /*#__PURE__*/ React__default.createElement("img", {
            src: `${imageUrl}?q=70&w=62&h=36&fit=fill&fm=jpeg`,
            alt: accessibleName
        })));
    })));
};

const VariantPickerContainer = (props)=>{
    const { dispatch  } = props;
    const handleVariantPick = React__default.useCallback((variant, href, event)=>{
        // Allow customers to open another variant in a new tab or window, otherwise prevent navigation
        if (!event || !isModifiedClickEvent(event)) {
            event === null || event === void 0 ? void 0 : event.preventDefault();
            dispatch(updateActiveVariant({
                sku: variant.sku
            }));
            window.history.replaceState(null, '', href);
        }
        ProductAnalyticsEvents.pushPdpVariantClickEvent({
            type: variant.productType,
            name: variant.name,
            id: variant.sku,
            dimension147: variant.description
        });
    }, [
        dispatch
    ]);
    return /*#__PURE__*/ React__default.createElement(VariantPicker, {
        channelAvailability: props.channelAvailability,
        variantItems: props.variantItems,
        activeVariantSku: props.activeVariantSku,
        activeVariantLabel: props.activeVariantLabel,
        formatProductUrl: props.formatProductUrl,
        onPick: handleVariantPick,
        stockCount: props.stockCount,
        isAllowBackOrder: props.isAllowBackOrder,
        enableStockIndicator: props.enableStockIndicator
    });
};

const mapGraphToClientVariants = (serverProduct, slug)=>{
    const { variants: serverVariants  } = serverProduct;
    if (!serverVariants.length) {
        return;
    }
    const clientVariants = serverVariants.map((serverVariant)=>{
        var ref;
        const price = getProductPrice(serverVariant);
        const { bridgeWidth , frameColor , frameSize , frameWidth , lensColor , lensHeight , lensWidth , templeLength , description: variantDescription , displayName ,  } = serverVariant;
        const { brand , description: productDescription  } = serverProduct;
        if (!price) {
            console.error(`Cannot render a variant that does not have a price`);
            return;
        }
        const frameColorName = Array.isArray(frameColor) ? frameColor.join(' / ') : frameColor;
        const colorFacet = [
            lensColor,
            frameColorName
        ].filter(Boolean).join(' / ');
        const description = [
            variantDescription,
            productDescription
        ].find(isString);
        const specifications = getProductSpecifications(serverVariant, brand);
        const productFeatures = getProductFeatures(serverVariant);
        const tags = getProductTags(serverVariant.tags);
        const media = getProductMedia(serverVariant.media);
        const metaTitle = getMetaTitle(serverVariant, serverProduct);
        const metaDescription = getMetaDescription(serverVariant, serverProduct);
        const ogImageUrl = getProductImageUrl(serverVariant.media);
        const frameMeasurements = getFrameMeasurements(bridgeWidth, frameWidth, lensHeight, lensWidth, templeLength);
        var _brandModelCode, _brandModelColorCode, _header, ref1, ref2, _recommendedCategoryKey;
        return {
            brandModelCode: (_brandModelCode = serverVariant.brandModelCode) !== null && _brandModelCode !== void 0 ? _brandModelCode : undefined,
            brandModelColorCode: (_brandModelColorCode = serverVariant.brandModelColorCode) !== null && _brandModelColorCode !== void 0 ? _brandModelColorCode : undefined,
            displayName,
            description,
            shortDescription: (_header = serverVariant.header) !== null && _header !== void 0 ? _header : undefined,
            sku: serverVariant.sku,
            slug,
            facets: {
                color: !!colorFacet ? colorFacet : undefined,
                size: (ref1 = frameSize === null || frameSize === void 0 ? void 0 : frameSize.size) !== null && ref1 !== void 0 ? ref1 : undefined
            },
            channelAvailability: serverVariant.channelAvailability,
            variantUsps: serverVariant.usps,
            tags,
            specifications,
            stock: {
                enableStockIndicator: !serverVariant.isExcludedFromCheckout,
                isAllowBackOrder: serverVariant.isAllowBackOrder,
                stockCount: (ref2 = (ref = serverVariant.inventory) === null || ref === void 0 ? void 0 : ref.totalQuantity) !== null && ref2 !== void 0 ? ref2 : 0
            },
            isVtoEnabled: serverVariant.isVtoEnabled,
            isReservable: serverVariant.isReservable,
            recommendedCategoryKey: (_recommendedCategoryKey = serverVariant.recommendedCategoryKey) !== null && _recommendedCategoryKey !== void 0 ? _recommendedCategoryKey : undefined,
            productFeatures,
            media,
            price,
            frameMeasurements,
            seoConfig: {
                metaTitle,
                metaDescription,
                ogImageUrl
            }
        };
    });
    return clientVariants.filter(isDefined);
};

const mapGraphProductToClientProduct = (serverProduct, slug)=>{
    const productType = getProductType(serverProduct.productType);
    const variants = mapGraphToClientVariants(serverProduct, slug);
    if (!variants || variants.length === 0) {
        return undefined;
    }
    const facetTree = consolidateParentFacets(variants);
    var _name, _taxCategory, _ratingsSummary;
    return {
        id: serverProduct.id,
        name: serverProduct.name,
        productType,
        brandName: serverProduct.brand,
        category: (_name = serverProduct.categories[0].name) !== null && _name !== void 0 ? _name : undefined,
        variants,
        facetTree,
        subscriptionProductSku: undefined,
        taxCategory: (_taxCategory = serverProduct.taxCategory) !== null && _taxCategory !== void 0 ? _taxCategory : undefined,
        ratingsSummary: (_ratingsSummary = serverProduct.ratingsSummary) !== null && _ratingsSummary !== void 0 ? _ratingsSummary : undefined,
        masterSku: variants[0].sku,
        //booleans
        isOfferedWithPrescriptions: !!serverProduct.isOfferedWithPrescriptions,
        isReviewEnabled: !!serverProduct.isReviewEnabled,
        isPublished: serverProduct.isPublished
    };
};

const GET_PRODUCT_BY_SLUG = gql`
  query GetProductBySlug($slug: String!, $facets: [SearchFacetInput!]!) {
    getProductBySlug(slug: $slug, facets: $facets) {
      product {
        ... on SunglassesProduct {
          brand
          description
          id
          isReviewEnabled
          name
          slug
          productType
          isOfferedWithPrescriptions
          isPublished
          metaTitle
          metaDescription
          categories {
            name
          }
          variants {
            inventory {
              totalQuantity
            }
            displayName
            sku
            brandModel
            brandModelCode
            brandModelColorCode
            description
            frameShape {
              displayName
              shape
            }
            frameSize {
              displayName
              size
            }
            faceShape {
              shape
            }
            frameMaterial
            segment
            metaTitle
            metaDescription
            header
            isAllowBackOrder
            isPolarized
            isReservable
            isGlazeble
            uvRaysProtection
            tags {
              key
              value
            }
            targetGender
            frameColor
            lensColor
            media {
              images {
                url
                dimensions {
                  w
                  h
                }
                label
              }
            }
            prices {
              originalPrice {
                currencyCode
                amount
              }
              discountedPrice {
                price {
                  amount
                  currencyCode
                }
                discountInformation {
                  value
                  name {
                    locale
                    value
                  }
                  type
                  description {
                    locale
                    value
                  }
                  validity {
                    validFrom
                    validUntil
                  }
                }
              }
            }
            isVtoEnabled
            isExcludedFromCheckout
            channelAvailability
            usps
            frameType
            opcoBackendId
            erpId
            lensWidth
            lensHeight
            templeLength
            frameWidth
            bridgeWidth
            recommendedCategoryKey
          }
          taxCategory {
            key
            rate {
              amount
              id
            }
          }
          ratingsSummary {
            average
            reviewCount
          }
        }
      }
      facets {
        key
        parentFacet
      }
    }
  }
`;

function getProductBySlug(_) {
    return _getProductBySlug.apply(this, arguments);
}
function _getProductBySlug() {
    _getProductBySlug = swcHelpers.asyncToGenerator(function*({ client , slug , locale  }) {
        const queryResult = yield client.query({
            query: GET_PRODUCT_BY_SLUG,
            variables: {
                slug,
                facets: []
            },
            context: {
                headers: {
                    'Accept-Language': locale
                }
            }
        });
        if (queryResult.error) {
            console.error(queryResult.error);
            throw queryResult.error;
        }
        const serverProduct = queryResult.data.getProductBySlug.product;
        if ((serverProduct === null || serverProduct === void 0 ? void 0 : serverProduct.__typename) !== 'SunglassesProduct') {
            console.error('This product type is not yet supported');
        }
        return mapGraphProductToClientProduct(serverProduct, slug);
    });
    return _getProductBySlug.apply(this, arguments);
}

const ProductDetailLayout = ()=>{
    const { Slots  } = useProductDetailLayoutSlots();
    const isMobile = MediaQuery.useIsBelow(MediaQuery.BreakpointName.TABLET_LANDSCAPE);
    const [ctaRef, ctaInview] = useInView();
    const showStickyButton = React.useMemo(()=>isMobile && !ctaInview
    , [
        isMobile,
        ctaInview
    ]);
    if (isMobile) {
        return /*#__PURE__*/ React.createElement("div", {
            className: "pdp-layout-mobile"
        }, /*#__PURE__*/ React.createElement(Slots.ProductTitle, null), /*#__PURE__*/ React.createElement(ProductDetailFullWidth, null, /*#__PURE__*/ React.createElement(Slots.ImageGallery, null)), /*#__PURE__*/ React.createElement(Slots.EcommContent, {
            ctaRef: ctaRef
        }), /*#__PURE__*/ React.createElement(Slots.PrimaryProductInfo, null), showStickyButton && /*#__PURE__*/ React.createElement(Slots.StickyCta, {
            className: "pdp-layout-mobile__sticky-cta"
        }), Slots.SecondaryProductInfo && /*#__PURE__*/ React.createElement(Slots.SecondaryProductInfo, null), /*#__PURE__*/ React.createElement(WebPageSchema, null));
    }
    return /*#__PURE__*/ React.createElement("div", {
        className: "pdp-layout"
    }, /*#__PURE__*/ React.createElement(ProductDetailContainedWidth, {
        className: "pdp-layout__sticky-scroll-wrapper"
    }, /*#__PURE__*/ React.createElement("div", {
        className: "pdp-layout__info"
    }, /*#__PURE__*/ React.createElement(Slots.ImageGallery, null), /*#__PURE__*/ React.createElement(Slots.PrimaryProductInfo, null)), /*#__PURE__*/ React.createElement(Sticky, {
        top: 0,
        bottomBoundary: '.pdp-layout__sticky-scroll-wrapper',
        enabled: true
    }, /*#__PURE__*/ React.createElement("aside", {
        className: "pdp-layout__ecomm-aside"
    }, /*#__PURE__*/ React.createElement(Slots.ProductTitle, null), /*#__PURE__*/ React.createElement(Slots.EcommContent, {
        className: "pdp-layout__ecomm-content"
    })))), Slots.SecondaryProductInfo && /*#__PURE__*/ React.createElement(Slots.SecondaryProductInfo, null), /*#__PURE__*/ React.createElement(WebPageSchema, null));
};
const ProductDetailFullWidth = ({ children , className  })=>/*#__PURE__*/ React.createElement("div", {
        className: classnames('pdp-layout--full-width', className)
    }, children)
;
const ProductDetailContainedWidth = ({ children , className  })=>/*#__PURE__*/ React.createElement("div", {
        className: classnames('pdp-layout--contained-width', className)
    }, children)
;

export { UserReviews as $, ProductImage as A, BrandPromotionContainer as B, ClickAndReserveCustomerFormProvider as C, Discount as D, ProductImageGalleryContainer as E, FrameMeasurementsContainer as F, ProductImageGalleryMobile as G, ProductImageGallery as H, ProductStructuredData as I, DittoContainer as J, PromoteInstallments as K, PromoteInstallmentsModalSlot as L, SizeGuideModalView as M, NonRxProductCTA as N, StarRatingReview as O, ProductAnalyticsEvents as P, StickyMobileCtaAndPrice as Q, RxGlassesCTA as R, SelectionBoxRadio as S, SunglassesRxableSelectContainer as T, UnpublishedProductEcommContentContainer as U, UnpublishedProductEcommContent as V, UnpublishedProductImageContainer as W, UnpublishedProductImage as X, SunglassesEcommContent as Y, TileLayout as Z, TryOnButton as _, useProductData as a, useVto as a0, VariantPickerContainer as a1, VirtualMirrorModal as a2, vtoEvent as a3, VtoProvider as a4, QuantityDiscountSelect as a5, useCartDiscounts as a6, CartDiscountType as a7, getCartDiscountAmount as a8, getTotalAvailableQuantity as a9, getFilteredQuantityDiscounts as aa, getMatchedDiscounts as ab, divideEvenlyInTwo as ac, getHighestMatchedDiscount as ad, MAX_QUANTITY_LIMIT as ae, getProductBySlug as af, CmsProvider as ag, actions as ah, reducer as ai, ProductProvider as aj, ProductDetailLayout as ak, ProductDetailContainedWidth as al, ProductDetailFullWidth as am, ProductDetailSlotsProvider as an, ProductDetailLayoutSlotsProvider as ao, useProductDetailSlots as ap, useProductDetailLayoutSlots as aq, getFrameMeasurements as ar, getActiveBrandPromotion as as, pushStoreClickEvent as b, pushChangeStoreClickEvent as c, useClickAndReserveCustomerForm as d, pushRequestReservationClickEvent as e, pushViewCustomerDetailsEvent as f, pushViewRequestConfirmationEvent as g, pushConfiguratorClickCloseEvent as h, pushConfiguratorClickBackEvent as i, pushUseMyCurrentLocationEvent as j, BrandPromotion as k, FrameMeasurements as l, PrescriptionAvailabilityIndicator as m, ProductDetailDescriptionContainer as n, ProductDetailDescription as o, pushViewFindInStoreEvent as p, ProductDetailFeaturesContainer as q, ProductDetailFeatures as r, ProductDetailPrice as s, ProductDetailSpecificationContainer as t, useClickAndReserveContext as u, ProductDetailTitleContainer as v, ProductDetailTitle as w, ProductDetailUspsContainer as x, ProductDetailUsps as y, ProductDiscountLabel as z };
//# sourceMappingURL=index-c6c86e83.js.map
